import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { AlertCircle, ArrowRight, ArrowUp, Check, CornerDownRight, ChevronDown, ChevronLeft, ChevronRight, Copy, FilePlus2, FileText, Hourglass, Loader2, Mic, Pencil, Play, Plus, ShieldCheck, Sparkles, Square, Workflow, X } from 'lucide-react'

import { cn } from '@/lib/utils'
import {
  IntuiDropdownMenu,
  IntuiDropdownMenuContent,
  IntuiDropdownMenuItem,
  IntuiDropdownMenuTrigger,
} from '@/components/intueri/IntuiDropdownMenu'

import { Checkbox } from '@/components/ui/checkbox'
import { AGENT_ALIASES, AgentId } from '@/constants/agentNames'
import { IntuiBadge } from '@/components/intueri/IntuiBadge/IntuiBadge'
import { toast } from 'sonner'
import { IntuiButton } from '@/components/intueri/IntuiButton'
import { IntuiInput } from '@/components/intueri/IntuiInput'
import { IntuiTextarea } from '@/components/intueri/IntuiTextarea'

import {
  enhanceWorkflowInstructions,
  getWorkflowRun,
  getWorkflowTemplateChat,
  listWorkflowRuns,
  saveWorkflowRunChat,
  saveWorkflowTemplateChat,
} from '../workflowApi'
import { WorkflowResultPanel, runOutcomeLabel } from '../WorkflowResultPanel'
import type {
  GenerateWorkflowDraftResponse,
  NormalizedToolCall,
  WorkflowClarifyingQuestion,
  WorkflowPlanPhase,
  WorkflowPlanPreview,
  WorkflowResolvedSources,
  WorkflowRunStatus,
  WorkflowRunView,
} from '../workflowTypes'
import { isV2Plan, retryFailedRunCopy, workflowStatusLabel } from '../workflowTypes'
import {
  getWorkflowSettings,
  knowledgeFileName,
  subscribeWorkflowSettings,
  updateWorkflowSettings,
} from '../workflowSettings'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import SelectFilesDialog from './SelectFilesDialog'
import {
  TROUBLESHOOTING_TERMINAL_STATUSES,
  resumeAcknowledgement,
  type TroubleshootingWorkflowController,
} from './useTroubleshootingWorkflow'

type ChatBlock =
  | { id: string; kind: 'seed' }
  | { id: string; kind: 'user'; text: string; at?: string }
  | { id: string; kind: 'assistant'; text: string }
  | {
      id: string
      kind: 'clarification'
      /** True for HITL review gates — renders "Review checkpoint", not "Clarification needed". */
      checkpoint?: boolean
      runId: string
      question: string
      /**
       * Agent-suggested one-click answers (candidate tags, document names, …).
       * Optional: transcripts persisted before this field existed (or with no
       * candidates) restore without it, so the renderer must tolerate undefined.
       */
      options?: string[]
    }
  /** The design's "Share your Google Meet link" card — takes the link the bot will join. */
  | { id: string; kind: 'meet_link' }
  /**
   * Figma 645:174716 / 645:175741: "Interview in progress" while the bot is on the call, then
   * "Processing" once it has left and the transcript is being turned into the method. One block
   * with a phase, because it is one wait from the user's point of view.
   */
  | { id: string; kind: 'interview_wait'; phase: 'in_progress' | 'processing' | 'timed_out' }
  /**
   * What the user supplied as the method, shown as itself. A bare "📄 name" line read as a
   * message the user typed; the file (or interview) they attached should be visible in the
   * transcript as an artifact, which is also what makes it obvious WHICH document the run is
   * about to follow.
   */
  | {
      id: string
      kind: 'attachment'
      name: string
      source: 'document' | 'interview'
      /** The method the run will follow, or data it will read — see `SuppliedMethodCard`. */
      role?: 'method' | 'data'
      documentId?: string
    }
  | {
      id: string
      kind: 'workflow_ready'
      /**
       * The run that existed when this card was pushed, so the card can tell a
       * FIRST run from a re-run. `Boolean(workflow.run)` alone was wrong after a
       * regenerate: the previous run is still loaded, so a brand-new plan's card
       * claimed "Run again" for something that had never run.
       */
      runIdAtCreation?: string | null
    }
  | {
      id: string
      kind: 'gen_question'
      // LEGACY (pre design-refresh) one-card-per-question ask — kept so
      // persisted transcripts restore; new sessions use 'gen_questions'.
      question: string
      options: Array<{ label: string; description?: string }>
      allowCustom: boolean
      index: number
      total: number
      answered?: string
    }
  | {
      id: string
      kind: 'gen_questions'
      // Cursor-style ask (Figma 8-edge-cases Multiple/Single Confirmation):
      // ONE card holding every question, with `< n of N >` navigation. The
      // composer below is the custom-answer input. `completed` collapses the
      // card into the question → answer summary (the Answered screen).
      questions: Array<{
        question: string
        options: Array<{ label: string; description?: string }>
        allowCustom: boolean
      }>
      answers: Array<string | undefined>
      index: number
      completed?: boolean
      /** The prompt that raised these questions — restores the live session after reload. */
      prompt?: string
      /** Section-9 suggestion behind question 0, if any — restored with the session. */
      suggestion?: { templateId: string; title: string; source?: 'builtin' | 'tenant' }
    }
  | {
      id: string
      kind: 'result'
      // Snapshot of a finished run's artifact, captured into the transcript so it
      // stays anchored chronologically (after the run that produced it) instead of
      // floating at the bottom — and so a second run in the same chat appends below
      // it. Persisted via chatLog, so historical results survive reload.
      runId: string
      status: string
      finalOutput: Record<string, unknown>
    }
  | {
      id: string
      kind: 'step'
      // Snapshot of a finished agent/verify step, streamed into the transcript as
      // a collapsible card (header + plain-language highlights + the tools it ran).
      // This is where the per-tool detail lives now that the canvas node is a clean
      // card. Lightweight strings only, so it persists in chatLog and restores.
      stepRunId: string
      title: string
      stepKind: string
      status: string
      highlights: string[]
      tools: Array<{ label: string; status: string }>
    }

// Random ids rather than a module-level counter: a mutable counter is shared
// across every mounted panel (and leaks across tests), while uuids stay
// collision-free without state.
const nextChatId = () => `workflow-chat-${crypto.randomUUID()}`

// Scroll-back across runs: how close to the top (px) triggers loading the
// previous run's transcript on demand.
const HISTORY_SCROLL_THRESHOLD_PX = 140

/**
 * The planning checklist's phase labels.
 *
 * ONE definition, used by every `runWithPlanningStages` call site AND by the read filter below,
 * because the filter has to recognise text a RETIRED writer stored: while these strings live in
 * two places, renaming a label silently un-filters every transcript that already holds the old
 * one (review on #29153).
 */
const PLANNING_LABELS = {
  understanding: 'Understanding your request',
  applying: 'Applying your answers',
  loadingTemplate: 'Loading the template',
  drafting: 'Drafting workflow steps',
  hitl: 'Configuring HITL checkpoints',
  validating: 'Validating the workflow',
} as const

/**
 * Recognise the retired `Planned: …` line in a transcript being restored.
 *
 * #29132 deleted the code that WROTE it, which fixes every new run and fixes nothing the user
 * is looking at: the transcript is persisted on the run (`state.chatLog`) and rehydrated on
 * open, so every run created before that deploy still replays "Planned: Understanding your
 * request · Drafting workflow steps · Configuring HITL checkpoints · Validating the workflow"
 * forever — reported on dev run `4eccb816` after the fix had landed.
 *
 * Filtered ON RENDER rather than migrated in the database — and rather than on hydration, which
 * is what the first version of this fix did: `setBlocks(filtered)` re-armed the debounced persist
 * effect, so merely OPENING an old run rewrote its stored transcript without the line (greptile
 * P1 on #29153). The stored log is the record of what happened; declining to draw our own copy of
 * it is the whole intervention.
 *
 * The writer joined EVERY label of its set, so the line always opens with the first label of one
 * of the three sets — `Understanding your request` (generate), `Applying your answers` (clarify
 * round) or `Loading the template` (template load). Anchored to the start of the text and to that
 * vocabulary, so a person who types a sentence beginning "Planned:" keeps every word of it.
 */
const RETIRED_PLANNING_HEADS = [
  PLANNING_LABELS.understanding,
  PLANNING_LABELS.applying,
  PLANNING_LABELS.loadingTemplate,
] as const

const RETIRED_PLANNING_LINE = new RegExp(
  `^Planned:\\s*(?:${RETIRED_PLANNING_HEADS.join('|')})`,
  'i'
)

/**
 * `text` is read off `state.chatLog`, which is server jsonb cast to `ChatBlock[]` rather than
 * validated — so a stored assistant block written by some older bundle without one must not throw
 * inside a render filter (review on #29153).
 */
function isRetiredPlanningLine(block: ChatBlock): boolean {
  return (
    block.kind === 'assistant' &&
    typeof block.text === 'string' &&
    RETIRED_PLANNING_LINE.test(block.text.trim())
  )
}

/** Inert callback for read-only history blocks (their controls never act). */
const noop = () => {}

/** One past run's transcript, loaded lazily and rendered read-only above the current one. */
type RunHistorySegment = { runId: string; label: string; blocks: ChatBlock[] }

/**
 * Divider label for a past run in the scroll-back history (date · outcome).
 *
 * The outcome reads from what the run DELIVERED (the shared decider behind the
 * result card and the report pill), not the engine's stored status — otherwise
 * the divider above a green result card still said "Completed · open questions"
 * for runs recorded before the audit fix, contradicting the card right under it.
 * Non-delivered runs (running, failed, guardrail, …) keep their status wording.
 */
function runHistoryLabel(
  run: Pick<WorkflowRunView, 'createdAt' | 'status' | 'finalOutput'>,
): string {
  const when = run.createdAt
    ? new Date(run.createdAt).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : 'Earlier run'
  return `${when} · ${runOutcomeLabel(run)}`
}

/** A thin labelled rule separating one run's transcript from the next. */
function RunHistoryDivider({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
      <span className="h-px flex-1 bg-border" />
      <span className="shrink-0">{label}</span>
      <span className="h-px flex-1 bg-border" />
    </div>
  )
}

/**
 * Premade prompt chips shown above the composer while the chat is still empty
 * (Figma "4. Premade Prompt"). Clicking one fills the composer with a template
 * the user completes; once real context exists (any message sent), they vanish.
 */
const PREMADE_PROMPTS: Array<{ label: string; prompt: string }> = [
  {
    label: 'Deviation Watch',
    prompt: 'Watch [variable or unit] and alert me when it drifts from normal. Every [1 hour].',
  },
  {
    label: 'Equipment Health',
    prompt: 'Check [equipment or unit] health and flag anything drifting from its baseline.',
  },
  {
    label: 'Troubleshoot',
    prompt: 'Help me troubleshoot [problem] on [equipment or unit], starting from the most recent alarms.',
  },
]

/**
 * Unfilled premade-prompt placeholders like "[variable or unit]". Sending one
 * as-is generates a workflow that can only run against the placeholder text —
 * today's guardrail-blocked runs traced back to exactly this. The content must
 * contain whitespace: template placeholders are phrases ("[variable or unit]",
 * "[1 hour]"), while a bracketed token WITHOUT spaces is legitimate user input
 * (ISA/P&ID tags like "[PIC-101]") and must never be blocked.
 */
export function unfilledPlaceholders(text: string): string[] {
  return [...text.matchAll(/\[([^\][]{0,30}\s[^\][]{0,30})\]/g)].map((match) => `[${match[1]}]`)
}

/**
 * Chat-native run command ("run it", "start the workflow", "execute") — the
 * chat starts the run the same way the Run Workflow pill does, instead of
 * routing an obvious command into draft generation.
 */
export function isRunCommand(text: string): boolean {
  const normalized = text.trim().toLowerCase().replace(/[.!?]+$/, '')
  return (
    /^(run|start|execute)( the)?( workflow| it)?( now)?$/.test(normalized) ||
    /^(please )?(run|start|execute) (the |this |my )?workflow( now)?$/.test(normalized)
  )
}

/**
 * The run-time "Evidence verification" toggle only composes a verify step for
 * the base troubleshooting flow (backend `composeVerification` is scoped to
 * `troubleshooting_base`). On an opened METHOD template (HAZOP/FMEA/…) it would
 * read active yet be a backend no-op, so the composer hides it there. A
 * null/absent active template is the default base flow (rahul8436 review).
 */
export function isVerificationApplicable(activeTemplateId: string | null | undefined): boolean {
  return (activeTemplateId ?? 'troubleshooting_base') === 'troubleshooting_base'
}

/** Appended to the outgoing generation prompt when Extended Thinking is on. */
/**
 * How long each completed stage holds before the next is ticked.
 *
 * ON THE CRITICAL PATH, and therefore LEFT ALONE at its original pace: the ticks are awaited so
 * that every phase visibly completes before the result card lands (a contract with its own test),
 * which makes every millisecond here latency the reader pays before seeing the answer. Slowing it
 * to 300ms pushed four stages past 1.2s and delayed the outcome.
 *
 * Item 14 is fixed by the DWELL below instead — it costs nothing, because it is detached.
 */
const IS_VITEST = Boolean(import.meta.env.VITEST)
const STAGE_TICK_MS = IS_VITEST ? 0 : 200
const FIRST_STAGE_HOLD_MS = IS_VITEST ? 0 : 2500
const PERSIST_DEBOUNCE_MS = IS_VITEST ? 0 : 600
/**
 * The suffix the removed "Extended Thinking" composer toggle used to append to a v1
 * generation prompt. Kept ONLY to strip it back off a restored chat intent written
 * before the toggle went away — nothing appends it any more.
 */
const EXTENDED_THINKING_SUFFIX =
  '\n\nTake extra time to think through scope, dependencies, HITL checkpoints, and failure handling before finalizing the workflow plan.'

const STARTING_RUN_LINE = 'Starting the workflow run…'
const RUN_ALREADY_GOING =
  'That run is already going — I will post the result here when it lands.'

function dropTrailingStarting(blocks: ChatBlock[]): ChatBlock[] {
  const last = blocks[blocks.length - 1]
  if (last?.kind === 'assistant' && last.text === STARTING_RUN_LINE) {
    return blocks.slice(0, -1)
  }
  return blocks
}

export function TroubleshootingWorkflowPanel({
  workflow,
  onAttachFile,
  onOpenTemplate,
}: {
  workflow: TroubleshootingWorkflowController
  /** Opens the Select Files modal (the "+" → Attach File edge case). */
  onAttachFile?: () => void
  /**
   * Reuse an EXISTING tenant workflow (template-ask "Yes" on a tenant
   * suggestion): navigate to it so hydration + chat restore reopen the saved
   * canvas and conversation. Absent → falls back to the preset preview.
   */
  onOpenTemplate?: (templateId: string) => void
}) {
  const [input, setInput] = useState(workflow.workflowPrompt)
  // Render-time mirror of `input` so async handlers (Enhance) can read the
  // LIVE composer text instead of their stale closure snapshot.
  const inputRef = useRef(input)
  inputRef.current = input
  const [blocks, setBlocks] = useState<ChatBlock[]>([
    { id: 'seed', kind: 'seed' },
  ])
  const [enhancing, setEnhancing] = useState(false)
  // Agentic checklist under the "Planning ▾" indicator (Figma Generating
  // screens): the REAL stages of the generation pipeline, checked off as the
  // flow passes them. Cleared when the outcome lands (the Done card takes over).
  const [planningStages, setPlanningStages] = useState<Array<{ label: string; done: boolean }>>([])
  // True while generation is parked on clarifying questions: the checklist
  // stays VISIBLE (progress so far, no spinner) instead of vanishing, and the
  // next runWithPlanningStages call (the answers round) resumes it live.
  const [planningPaused, setPlanningPaused] = useState(false)
  // Custom row on the question card focuses the composer — the composer IS
  // the custom-answer input (Figma has no input inside the card).
  const composerRef = useRef<HTMLTextAreaElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)

  // ── Attached files as composer chips ─────────────────────────────────────
  // The workflow's Knowledge files (Settings → Knowledge / "+" → Attach File)
  // surface on the input: max 2 chips + a "+N more" opener for the full-list
  // modal, so 100 attachments can't swallow the composer. Same settings key
  // as WorkflowBuilder/SettingsPanel; the subscription keeps chips live when
  // files attach from the dialog or the settings panel.
  const attachSettingsKey =
    workflow.openedTemplateId ?? workflow.draft?.definition.id ?? 'workflow-new'
  const [attachedFiles, setAttachedFiles] = useState<
    Array<string | { name: string; documentId?: string }>
  >(() => getWorkflowSettings(attachSettingsKey).knowledgeFiles)
  const [filesModalOpen, setFilesModalOpen] = useState(false)
  /** The "use a past interview" picker: the catalog, locked to transcripts. */
  const [interviewPickOpen, setInterviewPickOpen] = useState(false)
  useEffect(() => {
    setAttachedFiles(getWorkflowSettings(attachSettingsKey).knowledgeFiles)
    return subscribeWorkflowSettings((changedId) => {
      if (changedId === attachSettingsKey) {
        setAttachedFiles(getWorkflowSettings(attachSettingsKey).knowledgeFiles)
      }
    })
  }, [attachSettingsKey])
  // Nothing left to list — the modal must not linger as an empty shell.
  useEffect(() => {
    if (attachedFiles.length === 0) setFilesModalOpen(false)
  }, [attachedFiles.length])
  // Removal is POSITIONAL: two entries can share a display identity (a bare
  // 'report.pdf' string and { name: 'report.pdf' }), so filtering by identity
  // would silently delete both. The rendered list and the settings row read
  // from the same source, so the index is stable across the click.
  const removeAttachedFile = (index: number) => {
    const current = getWorkflowSettings(attachSettingsKey).knowledgeFiles
    updateWorkflowSettings(attachSettingsKey, {
      knowledgeFiles: current.filter((_, i) => i !== index),
    })
  }

  // ── Cross-run scroll-back history (read-only) ────────────────────────────
  // Each run keeps its OWN transcript; the chat opens on the latest run. To let
  // the user see earlier runs, we lazily fetch the previous run's transcript
  // when they scroll to the top and PREPEND it (oldest above). These segments
  // are display-only — never persisted back onto the current run (that would
  // scramble per-run storage), so the live `blocks` stay the current run alone.
  const [historySegments, setHistorySegments] = useState<RunHistorySegment[]>([])
  const [hasOlderRuns, setHasOlderRuns] = useState(false)
  const [historyExhausted, setHistoryExhausted] = useState(false)
  const [historyLoading, setHistoryLoading] = useState(false)
  // Older-run ids (newest→oldest) + how many we've pulled in so far.
  const olderRunsRef = useRef<{ id: string; label: string }[]>([])
  const olderCursorRef = useRef(0)
  // The current-run identity we've already built the older-run sequence for.
  const historyInitRef = useRef<string | null>(null)
  // scrollHeight captured just before a prepend, to hold the viewport steady.
  const historyAnchorRef = useRef<number | null>(null)
  // Synchronous in-flight guard: `historyLoading` state lags a render behind,
  // so rapid scroll events would slip past it and double-fetch the same run
  // (duplicate history segment). A ref flips immediately to block re-entry.
  const historyLoadingRef = useRef(false)
  const restoredRunIdRef = useRef<string | null>(null)
  const pendingChatRef = useRef<{ runId: string; blocks: ChatBlock[] } | null>(null)
  // Pre-run transcript persistence (a chat-built draft that was never run):
  // keyed by template instead of run. Once a run exists, the run transcript
  // owns the blocks and these are inert.
  const restoredTemplateChatRef = useRef<string | null>(null)
  const pendingTemplateChatRef = useRef<{ templateId: string; blocks: ChatBlock[] } | null>(null)
  // Active generation-time Q&A session (Cursor-style ask): the prompt that
  // triggered it plus the question/answer state mirrored into the single
  // 'gen_questions' block for rendering. Lives in a ref — restored
  // transcripts have no session, so their question cards render inert.
  const genSessionRef = useRef<{
    prompt: string
    blockId: string
    questions: WorkflowClarifyingQuestion[]
    answers: Array<string | undefined>
    index: number
    /** Set when questions[0] is the Section-9 template ask (Yes → presetHint / reopen). */
    suggestion?: { templateId: string; title: string; source?: 'builtin' | 'tenant' }
  } | null>(null)
  // Re-entrancy latch: a second option click in the same tick must not answer
  // the NEXT question with the first click's label (the ref mutation lands
  // before React flushes the disabled state onto the card).
  const genAnsweringRef = useRef(false)

  // ── Planning checklist pacing ─────────────────────────────────────────────
  // Generation is ONE opaque backend call (no per-phase server events), so
  // the checklist paces the real request: the first pending stage completes
  // on a short timer while the call runs, and once the draft lands the
  // remaining stages check off SEQUENTIALLY — all phases visibly finish
  // before the result card appears (the indicator stays mounted through the
  // completion animation via the stages-non-empty render condition).
  const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))
  const markStagesDone = (through: number) =>
    setPlanningStages((prev) => prev.map((stage, i) => (i <= through ? { ...stage, done: true } : stage)))
  const runWithPlanningStages = async <T,>(
    labels: Array<{ label: string; done?: boolean }>,
    call: () => Promise<T>,
    // false ⇒ the call ended in a clarify ask: only the first pending stage
    // completes (the later phases genuinely did not run).
    completedAllPhases: (result: T) => boolean
  ): Promise<T> => {
    setPlanningStages(labels.map(({ label, done }) => ({ label, done: done === true })))
    setPlanningPaused(false)
    const firstPending = labels.findIndex(({ done }) => done !== true)
    const timer =
      FIRST_STAGE_HOLD_MS > 0
        ? setTimeout(() => markStagesDone(firstPending), FIRST_STAGE_HOLD_MS)
        : undefined
    try {
      const result = await call()
      if (timer) clearTimeout(timer)
      if (completedAllPhases(result)) {
        // The ticks are AWAITED — every phase visibly completes before the result card lands,
        // which is a contract with its own test ("ALL planning phases visibly complete before the
        // result card lands"). Only the HOLD is detached.
        // Skip the 0-ms `setTimeout` in Vitest: `await sleep(0)` still queues macrotasks that
        // leak into the next test and trip the 5s timeout as a rotating flake.
        for (let i = Math.max(firstPending, 0); i < labels.length; i += 1) {
          markStagesDone(i)
          if (STAGE_TICK_MS > 0) await sleep(STAGE_TICK_MS)
        }
        // NO TRANSCRIPT LINE. The checklist animates and clears; nothing is left behind.
        //
        // What it used to leave was OUR phase vocabulary — "Configuring HITL checkpoints" is the
        // engineer-terminology defect fixed everywhere else — permanently, on every workflow a
        // person creates. Enforced by `ALL planning phases visibly complete before the result
        // card lands` in `TroubleshootingWorkflowPanel.test.tsx`, which asserts the absence on
        // THIS branch.
        setPlanningStages([])
      } else {
        // Clarify ask: the checklist STAYS — checked through what actually
        // ran, idle (no spinner) while the user answers. The answers round
        // re-seeds it and the run continues visibly.
        markStagesDone(firstPending)
        setPlanningPaused(true)
      }
      return result
    } catch (err) {
      setPlanningStages([])
      setPlanningPaused(false)
      throw err
    } finally {
      if (timer) clearTimeout(timer)
    }
  }

  // A RESTORED transcript that ends on an unanswered question card must
  // resume the live session — without this the card rendered inert (summary
  // view), so its options and Custom input were dead after any reload.
  const rehydrateGenSession = (restored: ChatBlock[]) => {
    const lastGen = [...restored]
      .reverse()
      .find((block): block is Extract<ChatBlock, { kind: 'gen_questions' }> =>
        block.kind === 'gen_questions'
      )
    if (!lastGen || lastGen.completed) return
    const lastUser = [...restored]
      .reverse()
      .find((block): block is Extract<ChatBlock, { kind: 'user' }> => block.kind === 'user')
    genSessionRef.current = {
      // Older persisted blocks predate the stored prompt — the last user
      // message is the request that raised the questions.
      prompt: lastGen.prompt ?? lastUser?.text ?? '',
      blockId: lastGen.id,
      questions: lastGen.questions,
      answers: [...lastGen.answers],
      index: lastGen.index,
      ...(lastGen.suggestion ? { suggestion: lastGen.suggestion } : {}),
    }
  }
  const awaitingClarification = workflow.run?.status === 'waiting_for_user'
  const busy = workflow.loading || workflow.starting || workflow.resuming
  // A run actively executing (statuses stream onto the canvas nodes).
  // runPending bridges navigate→first-load so the composer's run state
  // doesn't blip while the just-started run is still fetching.
  const runInFlight =
    workflow.starting ||
    workflow.resuming ||
    workflow.runPending ||
    workflow.run?.status === 'queued' ||
    workflow.run?.status === 'running'
  // The Stop square cancels a live RUN; draft generation has no cancel seam,
  // so it stays disabled (spinner semantics) during generate.
  const cancellableRun = Boolean(
    workflow.run && !TROUBLESHOOTING_TERMINAL_STATUSES.has(workflow.run.status)
  )
  // Premade prompts disappear once the user has put real context in (any
  // message beyond the seed, or an open run).
  const showPremade = blocks.length <= 1 && !workflow.run
  const failedRunRetry =
    workflow.run && ['failed', 'blocked', 'budget_limited'].includes(workflow.run.status)
      ? retryFailedRunCopy(workflow.run)
      : null
  // Note: genSessionRef is a ref, but every question push also setBlocks —
  // the re-render reads the fresh value.
  // The placeholder is the main next-action cue, so it has to track STATE: a user
  // who finished a workflow and still read "describe the workflow" kept describing
  // (demo call, Tom Chunat 7:00 — FS item 2). Once something runnable exists the cue
  // becomes running it, never describing again.
  const hasRunnableWorkflow = Boolean(workflow.planPreview || workflow.openedTemplateId)
  // "View Report" from a finished run's card (FS item 6). Navigation lives on the
  // CONTROLLER, which already owns routing — a useNavigate() here would make the
  // panel unrenderable outside a Router, which several of its tests are.
  const onViewReport = workflow.openReport

  const composerPlaceholder = genSessionRef.current
    ? 'Type a custom answer, or pick an option above...'
    : awaitingClarification
      ? workflow.clarificationKind === 'review_checkpoint'
        ? // Putra's HITL frame copy: at a review GATE the composer is the correction
          // path, so it asks for the correction rather than for an answer.
          'Tell me what to change?'
        : 'Answer the clarification question...'
      : runInFlight
        ? 'The workflow is running — ask a question or wait for the result...'
        : hasRunnableWorkflow
          ? 'Run the workflow, or tell me what to change...'
          : 'Describe your workflow...'

  useEffect(() => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: 'smooth',
    })
  }, [blocks])

  // Build the older-run sequence once per opened run: list this workflow's runs
  // (newest→oldest) and keep the ones OLDER than the one we're viewing as the
  // scroll-back backlog. Resets when a different run/workflow opens.
  useEffect(() => {
    const currentRunId = workflow.run?.id
    const templateId = workflow.run?.templateId ?? workflow.openedTemplateId
    if (!currentRunId || !templateId) return
    if (historyInitRef.current === currentRunId) return
    historyInitRef.current = currentRunId
    setHistorySegments([])
    setHistoryExhausted(false)
    setHasOlderRuns(false)
    olderRunsRef.current = []
    olderCursorRef.current = 0
    // Scroll-back reaches the last 100 runs. Beyond that is silently excluded
    // until the runs endpoint grows a pagination cursor (tracked follow-up).
    void listWorkflowRuns({ templateId, limit: 100 })
      .then(({ runs }) => {
        if (historyInitRef.current !== currentRunId) return // a newer open won
        const idx = runs.findIndex((r) => r.id === currentRunId)
        const older = (idx >= 0 ? runs.slice(idx + 1) : runs.filter((r) => r.id !== currentRunId)).map(
          (r) => ({ id: r.id, label: runHistoryLabel(r) })
        )
        olderRunsRef.current = older
        setHasOlderRuns(older.length > 0)
        if (older.length === 0) setHistoryExhausted(true)
      })
      .catch(() => setHistoryExhausted(true))
  }, [workflow.run?.id, workflow.run?.templateId, workflow.openedTemplateId])

  // Load the next-older run's transcript and prepend it (oldest above). Guarded
  // so overlapping scroll events don't double-fetch; capped by the sequence.
  const loadOlderRun = useCallback(async () => {
    // Ref guard (synchronous) over the state guard: blocks re-entry within the
    // same render cycle so rapid scroll events can't double-fetch the same run.
    if (historyLoadingRef.current || historyExhausted) return
    const cursor = olderCursorRef.current
    const older = olderRunsRef.current
    if (cursor >= older.length) {
      setHistoryExhausted(true)
      return
    }
    const { id, label } = older[cursor]
    historyLoadingRef.current = true
    setHistoryLoading(true) // drives the spinner; the ref drives the guard
    historyAnchorRef.current = scrollRef.current?.scrollHeight ?? null
    try {
      const run = await getWorkflowRun(id)
      const chatLog = (run?.state?.chatLog as ChatBlock[] | undefined) ?? []
      olderCursorRef.current = cursor + 1
      setHistorySegments((prev) => [{ runId: id, label, blocks: chatLog }, ...prev])
      if (olderCursorRef.current >= older.length) setHistoryExhausted(true)
    } catch {
      historyAnchorRef.current = null // leave the cursor so a later scroll retries
    } finally {
      historyLoadingRef.current = false
      setHistoryLoading(false)
    }
  }, [historyExhausted])

  // After older content is prepended, nudge scrollTop by exactly the added
  // height so the viewport stays put (no jump) — the infinite-scroll anchor.
  useLayoutEffect(() => {
    const anchor = historyAnchorRef.current
    const el = scrollRef.current
    if (anchor == null || !el) return
    const delta = el.scrollHeight - anchor
    if (delta > 0) el.scrollTop += delta
    historyAnchorRef.current = null
  }, [historySegments])

  const handleTranscriptScroll = () => {
    if ((scrollRef.current?.scrollTop ?? Infinity) < HISTORY_SCROLL_THRESHOLD_PX) {
      void loadOlderRun()
    }
  }

  // Restore the PRE-RUN transcript once per opened template (?templateId with
  // no run to resume): a chat-built draft's conversation survives reopen. The
  // seed-only guard means a transcript the user already started this session
  // (or one restored from a run) is never clobbered.
  useEffect(() => {
    const chatTemplateId = workflow.openedTemplateId
    if (!chatTemplateId || workflow.runId) return
    if (restoredTemplateChatRef.current === chatTemplateId) return
    void getWorkflowTemplateChat(chatTemplateId)
      .then(({ chatLog }) => {
        // Mark restored only on SUCCESS — marking up-front would lock the
        // guard after a transient fetch failure and the transcript would
        // never be retried for the rest of the mount.
        restoredTemplateChatRef.current = chatTemplateId
        if (!Array.isArray(chatLog) || chatLog.length === 0) return
        // Ref mutation OUTSIDE the updater: React may invoke updaters more
        // than once (StrictMode/concurrent). rehydrateGenSession early-returns
        // when the log carries no incomplete gen_questions block, so running
        // it even when the updater keeps `prev` is harmless.
        // STORED AS STORED. The retired planning line is dropped where the transcript is
        // DRAWN, not here: hydrating a filtered array re-arms the debounced persist effect and
        // rewrites the run's stored log on mere open (greptile P1 on #29153).
        rehydrateGenSession(chatLog as ChatBlock[])
        setBlocks((prev) => {
          if (!(prev.length <= 1 && prev[0]?.kind === 'seed')) return prev
          return chatLog as ChatBlock[]
        })
      })
      .catch(() => {})
  }, [workflow.openedTemplateId, workflow.runId])

  // Restore the persisted transcript once per run. When a run loads carrying a
  // saved chatLog, hydrate the blocks from it; otherwise just mark the run as
  // restored so the persist effect below begins saving the live transcript.
  // restoredRunIdRef guards against poll/SSE-driven run updates clobbering
  // blocks the user has added since the initial restore.
  useEffect(() => {
    const runId = workflow.run?.id
    if (!runId || restoredRunIdRef.current === runId) return
    restoredRunIdRef.current = runId
    const saved = workflow.run?.state?.chatLog
    if (Array.isArray(saved) && saved.length > 0) {
      // Stored as stored — see the pre-run restore above.
      rehydrateGenSession(saved as ChatBlock[])
      setBlocks(saved as ChatBlock[])
    }
  }, [workflow.run])

  // When a run finishes, drop its result into the transcript as a chronological
  // block (once per run) rather than pinning a single result at the bottom. This
  // keeps the result anchored after the run that produced it, so starting another
  // troubleshooting in the same chat appends below it instead of stranding the
  // prior result at the end. Runs after the restore effect so a restored chatLog
  // that already carries the result block wins (the guard then no-ops).
  useEffect(() => {
    const run = workflow.run
    if (!run?.finalOutput || !TROUBLESHOOTING_TERMINAL_STATUSES.has(run.status)) return
    const runId = run.id
    const finalOutput = run.finalOutput as Record<string, unknown>
    const status = run.status
    setBlocks((prev) => {
      if (prev.some((block) => block.kind === 'result' && block.runId === runId)) return prev
      return [...prev, { id: nextChatId(), kind: 'result', runId, status, finalOutput }]
    })
  }, [workflow.run])

  // Any stopped run must explain itself in the chat — with or without an
  // artifact. A blocked run whose steps all succeeded used to render ONLY a
  // red "Error Executing Steps" pill (the audit's reason was discarded
  // server-side and this block was gated to artifact-less deaths).
  // `error.reason` carries the completion-audit cause (evidence shortfall,
  // guardrail, missing keys); `error.message` carries hard failures.
  // Deterministic block id dedupes across poll ticks.
  useEffect(() => {
    const run = workflow.run
    if (!run) return
    if (!['failed', 'blocked', 'guardrail_stopped', 'budget_limited'].includes(run.status)) return
    const blockId = `run-error-${run.id}`
    const err = run.error as Record<string, unknown> | null | undefined
    const detail = (['reason', 'message'] as const)
      .map((key) => (typeof err?.[key] === 'string' ? (err[key] as string).trim() : ''))
      .find((value) => value.length > 0)
    const label = workflowStatusLabel(run.status)
    const text = detail
      ? `The run stopped — ${label}: ${detail}`
      : run.finalOutput
        ? `The run stopped — ${label}. Review the report for what was and wasn't established.`
        : `The run stopped — ${label}. Open a node's details for step-level errors.`
    setBlocks((prev) =>
      prev.some((block) => block.id === blockId)
        ? prev
        : [...prev, { id: blockId, kind: 'assistant', text }]
    )
  }, [workflow.run])

  // Persist the transcript onto the run (debounced) so it survives reload.
  // Keyed on blocks + the run id (a primitive — so it re-arms only when the
  // transcript changes or a different run is opened, not on every poll tick).
  // Best-effort: a failed save never surfaces, and the next change retries.
  // pendingChatRef holds the latest (runId, blocks) so the unmount flush below
  // can save them even if the user navigates away inside the debounce window.
  useEffect(() => {
    const runId = workflow.run?.id
    if (!runId) return
    if (blocks.length <= 1 && blocks[0]?.kind === 'seed') return
    pendingChatRef.current = { runId, blocks }
    if (PERSIST_DEBOUNCE_MS <= 0) {
      void saveWorkflowRunChat(runId, blocks).catch(() => {})
      pendingChatRef.current = null
      return
    }
    const handle = setTimeout(() => {
      void saveWorkflowRunChat(runId, blocks).catch(() => {})
      pendingChatRef.current = null
    }, PERSIST_DEBOUNCE_MS)
    return () => clearTimeout(handle)
  }, [blocks, workflow.run?.id])

  // Persist the PRE-RUN transcript keyed by the OPENED template (debounced,
  // best-effort). A blank `/workflow/new` must not write this session's chat
  // onto a colliding draft id (same question → same slug → the last
  // conversation). Once a run starts, the run transcript takes over.
  const draftChatTemplateId = workflow.openedTemplateId
  useEffect(() => {
    // Gate on BOTH run signals: the ?runId param (present before the run
    // object finishes loading — same signal the restore effect uses) and the
    // loaded run. Otherwise a pre-run transcript could re-save during the
    // brief load window of a run that already owns the conversation.
    if (workflow.runId || workflow.run?.id || !draftChatTemplateId) return
    if (blocks.length <= 1 && blocks[0]?.kind === 'seed') return
    pendingTemplateChatRef.current = { templateId: draftChatTemplateId, blocks }
    if (PERSIST_DEBOUNCE_MS <= 0) {
      void saveWorkflowTemplateChat(draftChatTemplateId, blocks).catch(() => {})
      pendingTemplateChatRef.current = null
      return
    }
    const handle = setTimeout(() => {
      void saveWorkflowTemplateChat(draftChatTemplateId, blocks).catch(() => {})
      pendingTemplateChatRef.current = null
    }, PERSIST_DEBOUNCE_MS)
    return () => clearTimeout(handle)
  }, [blocks, workflow.runId, workflow.run?.id, draftChatTemplateId])

  // Flush any debounced-but-unsaved transcript on unmount (in-app navigation),
  // so leaving the builder right after a message doesn't drop it.
  useEffect(() => {
    return () => {
      const pending = pendingChatRef.current
      if (pending) void saveWorkflowRunChat(pending.runId, pending.blocks).catch(() => {})
      const pendingTemplate = pendingTemplateChatRef.current
      if (pendingTemplate)
        void saveWorkflowTemplateChat(pendingTemplate.templateId, pendingTemplate.blocks).catch(
          () => {}
        )
    }
  }, [])

  useEffect(() => {
    if (!awaitingClarification || !workflow.run) return
    // Capture the narrowed id into a local: the setBlocks closure below loses
    // the `workflow.run` non-null narrowing across the callback boundary.
    const runId = workflow.run.id
    const question = workflow.clarificationQuestion.trim()
    if (!question) return
    const clarificationKey = `${runId}:${question}`
    setBlocks((prev) => {
      if (
        prev.some(
          (block) =>
            block.kind === 'clarification' &&
            `${block.runId}:${block.question}` === clarificationKey
        )
      ) {
        return prev
      }
      return [
        ...prev,
        {
          id: nextChatId(),
          kind: 'clarification',
          runId,
          question,
          options: workflow.clarificationOptions,
          checkpoint: workflow.clarificationKind === 'review_checkpoint',
        },
      ]
    })
  }, [awaitingClarification, workflow.clarificationQuestion, workflow.clarificationOptions, workflow.run])

  /**
   * Answer the intake with a document: echo what was sent, extract + resume, then say what happened.
   *
   * The extracted text is NOT echoed — a whole SOP in the transcript buries the conversation, and
   * the file name is what the person recognises. Nothing keeps the text: it goes to the resume call
   * and is gone with this handler's scope.
   */
  const supplyMethodDocument = async (file: File) => {
    if (busy) return
    // The artifact card is pushed by the effect below, off `workflow.lastSuppliedMethod`, so a
    // method supplied from ANOTHER affordance (Add Files, while this ask is parked) shows up too.
    const outcome = await workflow.supplyMethodDocument(file)
    setBlocks((prev) => [...prev, { id: nextChatId(), kind: 'assistant', text: outcome.message }])
  }

  /**
   * "Get on a call": ask for the link the design asks for, then send the notetaker in.
   *
   * Figma "Interview / Upload files" (645:173503): the agent replies "I'll set up the call, please
   * share your Google Meet link" and a card takes the link. The call itself is the meeting bot;
   * the transcript it leaves behind is what answers the question afterwards.
   */
  const requestMethodCall = () => {
    if (busy) return
    // One link card per ask. Clicking the option again used to append another, each with its own
    // "sent" guard — so two cards meant two notetakers in the call (greptile P1 on #29843).
    if (blocks.some((block) => block.kind === 'meet_link')) return
    setBlocks((prev) => [
      ...prev,
      { id: nextChatId(), kind: 'user', text: GET_ON_A_CALL, at: new Date().toISOString() },
      {
        id: nextChatId(),
        kind: 'assistant',
        text: "I'll set up the call, please share your Google Meet link",
      },
      { id: nextChatId(), kind: 'meet_link' },
    ])
  }

  const submitMeetLink = async (url: string): Promise<boolean> => {
    if (busy) return false
    setBlocks((prev) => [
      ...prev,
      { id: nextChatId(), kind: 'user', text: url, at: new Date().toISOString() },
    ])
    const outcome = await workflow.startMethodCall(url)
    setBlocks((prev) => [...prev, { id: nextChatId(), kind: 'assistant', text: outcome.message }])
    // The card unlocks on a refused link instead of stranding the user; once the call HAS
    // started it stays locked, because a second send would put a second notetaker in it.
    if (!outcome.ok || !outcome.startedAt) return false

    // The design's wait (645:174716): the run stays parked and this card holds the screen until
    // the call produces its transcript. The user is NOT sent off to find it afterwards.
    const waitId = nextChatId()
    setBlocks((prev) => [...prev, { id: waitId, kind: 'interview_wait', phase: 'in_progress' }])
    const setPhase = (phase: 'in_progress' | 'processing' | 'timed_out') =>
      setBlocks((prev) =>
        prev.map((block) =>
          block.id === waitId && block.kind === 'interview_wait' ? { ...block, phase } : block
        )
      )

    const transcript = await workflow.awaitInterviewTranscript(outcome.startedAt, {
      // Correlate on the call, not on time: two interviews in one tenant would otherwise race
      // for "newest transcript since I started".
      meetingToken: outcome.meetingToken,
    })
    if (!transcript) {
      setPhase('timed_out')
      // The CALL started, so the card stays locked — the transcript is what did not arrive.
      return true
    }
    // 645:175741 — the agent flips to Processing while the transcript becomes the method.
    setPhase('processing')
    const supplied = await workflow.supplyMethodInterview(transcript.id, transcript.name)
    setBlocks((prev) =>
      prev
        .filter((block) => block.id !== waitId)
        .concat({ id: nextChatId(), kind: 'assistant', text: supplied.message })
    )
    return true
  }

  /**
   * Answer the intake with a PAST INTERVIEW. Same shape as supplyMethodDocument: echo what the
   * user chose, resume, then post the outcome as the assistant's reply.
   */
  const supplyMethodInterview = async (documentId: string, name: string) => {
    if (busy) return
    const outcome = await workflow.supplyMethodInterview(documentId, name)
    setBlocks((prev) => [...prev, { id: nextChatId(), kind: 'assistant', text: outcome.message }])
  }

  // Show WHAT answered the intake, wherever it came from. Keyed on the controller's record so a
  // re-render cannot duplicate the card and a second affordance cannot skip it.
  const echoedMethodRef = useRef<string | null>(null)
  useEffect(() => {
    const supplied = workflow.lastSuppliedMethod
    if (!supplied || echoedMethodRef.current === supplied.key) return
    echoedMethodRef.current = supplied.key
    setBlocks((prev) => [
      ...prev,
      {
        id: nextChatId(),
        kind: 'attachment',
        name: supplied.name,
        source: supplied.source,
        role: supplied.role,
      },
    ])
  }, [workflow.lastSuppliedMethod])

  // Submit a clarification answer (from the composer free-text OR an option
  // chip): echo it as a user bubble, resume the run, then post the outcome.
  const submitClarificationAnswer = async (text: string) => {
    const answer = text.trim()
    if (!answer || busy) return
    setBlocks((prev) => [
      ...prev,
      { id: nextChatId(), kind: 'user', text: answer, at: new Date().toISOString() },
    ])
    // WHAT WAS BEING ASKED BEFORE THIS REPLY, read before the resume so it is the OLD question. A
    // run that comes back parked on the SAME card is waiting for something (a file), not moving on
    // — and "Noted. Next: <the question you just answered>" reads as having been ignored.
    // THE CARD, not its wording — see `nextAskSlotFor`. Read from the hook's own run state, which
    // is the state BEFORE this reply, which is exactly what "was it this card?" needs.
    const askedBefore = {
      ...(workflow.clarificationSlot === undefined ? {} : { slot: workflow.clarificationSlot }),
      prompt: workflow.clarificationQuestion,
    }
    const resumed = await workflow.resumeRun(answer)
    // SAY WHAT THE RUN IS DOING WITH IT, which is not always "continuing" and is never "done
    // asking" — see `resumeAcknowledgement`, which owns all four wordings so the panel cannot
    // grow a fifth.
    setBlocks((prev) => [
      ...prev,
      {
        id: nextChatId(),
        kind: 'assistant',
        text: resumeAcknowledgement(resumed, workflow.lastResumeError?.current, askedBefore),
      },
    ])
  }


  /** Push the post-generation outcome blocks (shared by first call + re-call).
   *  `chatIntent` (NL-authored paths only): seeds the Settings→Instructions
   *  field from the chat prompt (Putra "10. Update": "When user create using
   *  Chat, Instruction should be filled by the chat intent") — only when the
   *  field is still empty, so a hand-edited value is never clobbered.
   *
   *  AND ONLY FROM THE FIRST AUTHORING TURN. Jenna, feedback doc item 15: "my whole
   *  last query was added as an instruction and it didn't make sense… I followed up
   *  saying 'no, this information is present in the document' and that whole sentence
   *  was added as an instruction." Putra confirmed the intent — instructions are
   *  AI-authored from the problem, not whatever the user last typed — so a follow-up
   *  is excluded here rather than filtered by its wording. A follow-up is any turn
   *  where an authored draft already existed: `draftIsAuthored` is true after a
   *  successful generate, and false for a canonical fallback from a failed first try. */
  const pushGenerationOutcome = (
    draft: GenerateWorkflowDraftResponse | null,
    generated: boolean,
    chatIntent?: string,
    isFollowUp = false
  ) => {
    const intent = chatIntent?.replace(EXTENDED_THINKING_SUFFIX, '').trim()
    const draftId = draft?.definition?.id
    if (
      generated &&
      !isFollowUp &&
      draftId &&
      intent &&
      !getWorkflowSettings(draftId).instructions.trim()
    ) {
      updateWorkflowSettings(draftId, { instructions: intent })
    }
    setBlocks((prev) => [
      ...prev,
      ...(!draft
        ? ([
            {
              id: nextChatId(),
              kind: 'assistant',
              text: 'I could not generate the workflow draft. Check the error above and try again.',
            },
          ] as const)
        : generated
          ? ([
              {
                id: nextChatId(),
                kind: 'workflow_ready',
                runIdAtCreation: workflow.run?.id ?? null,
              },
            ] as const)
          : ([
              {
                id: nextChatId(),
                kind: 'assistant',
                text: "I couldn't generate a custom workflow from that, so I loaded the standard troubleshooting workflow as a starting point. Edit it, or try a more specific description.",
              },
              {
                id: nextChatId(),
                kind: 'workflow_ready',
                runIdAtCreation: workflow.run?.id ?? null,
              },
            ] as const)),
    ])
  }

  /** Mirror the session's question state into its single chat block. */
  const syncGenBlock = (completed = false) => {
    const session = genSessionRef.current
    if (!session) return
    setBlocks((prev) =>
      prev.map((block) =>
        block.kind === 'gen_questions' && block.id === session.blockId
          ? { ...block, answers: [...session.answers], index: session.index, completed }
          : block
      )
    )
  }

  // Seeds the clarify cards from the v1 generator's questions. LIVE ONLY UNDER V1: the v2 path
  // never calls the generator, so nothing new reaches this there — but a v1 tenant's send still
  // walks these, and a RESTORED transcript from either engine can end on an unanswered card.
  const pushGenQuestions = (
    questions: WorkflowClarifyingQuestion[],
    prompt: string,
    suggestion?: {
      templateId: string
      title: string
      description: string
      source?: 'builtin' | 'tenant'
    }
  ) => {
    const id = nextChatId()
    // Section-9 template ask leads the card: "This looks like an FMEA…" for a
    // shipped template, or "You already have …" for the tenant's own workflow
    // (reuse over regenerating a near-duplicate).
    const isTenant = suggestion?.source === 'tenant'
    const allQuestions: WorkflowClarifyingQuestion[] = suggestion
      ? [
          {
            question: isTenant
              ? `You already have a workflow for this: "${suggestion.title}". Want to continue with it?`
              : `This looks like a ${suggestion.title}. Want to use that template?`,
            options: [
              {
                label: isTenant
                  ? `Yes, open ${suggestion.title}`
                  : `Yes, use the ${suggestion.title} template`,
                description: suggestion.description,
              },
              { label: 'No, build something else', description: 'Start from your own description' },
            ],
            allowCustom: false,
          },
          ...questions,
        ]
      : questions
    genSessionRef.current = {
      prompt,
      blockId: id,
      questions: allQuestions,
      answers: allQuestions.map(() => undefined),
      index: 0,
      ...(suggestion
        ? {
            suggestion: {
              templateId: suggestion.templateId,
              title: suggestion.title,
              ...(suggestion.source ? { source: suggestion.source } : {}),
            },
          }
        : {}),
    }
    const sessionQuestions = genSessionRef.current.questions
    setBlocks((prev) => [
      ...prev,
      {
        id,
        kind: 'gen_questions',
        prompt,
        ...(suggestion
          ? {
              suggestion: {
                templateId: suggestion.templateId,
                title: suggestion.title,
                ...(suggestion.source ? { source: suggestion.source } : {}),
              },
            }
          : {}),
        questions: sessionQuestions.map((question) => ({
          question: question.question,
          options: question.options,
          allowCustom: question.allowCustom,
        })),
        answers: sessionQuestions.map(() => undefined),
        index: 0,
      },
    ])
  }

  /** `< n of N >` navigation between the card's questions (view-only). */
  const navigateGenQuestion = (delta: number) => {
    const session = genSessionRef.current
    if (!session) return
    session.index = Math.min(Math.max(session.index + delta, 0), session.questions.length - 1)
    syncGenBlock()
  }

  // Answer the question the card is SHOWING (option click or typed text).
  // Advances to the next unanswered question; after the last one, the card
  // collapses into the Q→A summary and generation re-runs with the trail.
  const answerGenQuestion = async (answer: string) => {
    const session = genSessionRef.current
    if (!session || busy || genAnsweringRef.current) return
    genAnsweringRef.current = true
    try {
      session.answers = session.answers.map((existing, i) =>
        i === session.index ? answer : existing
      )
      // Template ask (question 0): Yes short-circuits to the prefilled
      // template; No falls through to the normal clarify walk.
      if (session.suggestion && session.index === 0 && answer.startsWith('Yes')) {
        const { templateId, title, source } = session.suggestion
        syncGenBlock(true)
        genSessionRef.current = null
        if (source === 'tenant' && onOpenTemplate && workflow.openedTemplateId) {
          // Reopen only when this chat already belongs to a workflow. Create
          // (`/workflow/new`) must not load that conversation for the same
          // question — the person already chose a fresh start.
          setPlanningStages([])
          setPlanningPaused(false)
          setBlocks((prev) => [
            ...prev,
            { id: nextChatId(), kind: 'assistant', text: `Opening "${title}"…` },
          ])
          onOpenTemplate(templateId)
          return
        }
        const { draft, generated } = await runWithPlanningStages(
          [{ label: PLANNING_LABELS.loadingTemplate }, { label: PLANNING_LABELS.validating }],
          () => workflow.generateDraft('workflow_chat', undefined, { presetHint: templateId }),
          () => true
        )
        pushGenerationOutcome(draft, generated)
        return
      }
      const nextUnanswered = session.answers.findIndex((entry) => entry === undefined)
      if (nextUnanswered >= 0) {
        session.index = nextUnanswered
        syncGenBlock()
        return
      }
      syncGenBlock(true)
      const pairs = session.questions.map((question, i) => ({
        question: question.question,
        answer: session.answers[i] as string,
      }))
      const prompt = session.prompt
      genSessionRef.current = null
      const { draft, generated } = await runWithPlanningStages(
        [
          { label: PLANNING_LABELS.applying, done: true },
          { label: PLANNING_LABELS.drafting },
          { label: PLANNING_LABELS.hitl },
          { label: PLANNING_LABELS.validating },
        ],
        () => workflow.generateDraft('workflow_chat', prompt, { clarificationAnswers: pairs }),
        () => true
      )
      pushGenerationOutcome(draft, generated, prompt)
    } finally {
      genAnsweringRef.current = false
    }
  }

  // "+" → Enhance Prompt: rewrite the composer text with the native
  // synthesizer; the user reviews the result before sending (nothing is sent).
  // The original is NEVER lost: the toast offers Undo, and a null result
  // (model unavailable, degenerate rewrite filtered server-side) keeps the
  // text with an honest notice instead of a silent no-op.
  const enhancePrompt = async () => {
    // Undo must restore the input EXACTLY as typed (whitespace included) —
    // only the text sent to the enhancer is trimmed.
    const original = input
    const toEnhance = original.trim()
    if (!toEnhance || enhancing) return
    setEnhancing(true)
    try {
      const { enhanced } = await enhanceWorkflowInstructions(toEnhance)
      // The composer stays editable while the request is in flight — if the
      // user kept typing, their newer text wins and the stale enhancement is
      // dropped (never clobber live edits with an old snapshot).
      if (inputRef.current !== original) {
        toast.info('You kept typing — kept your text over the enhancement.')
      } else if (enhanced) {
        setInput(enhanced)
        toast.success('Prompt enhanced', {
          action: { label: 'Undo', onClick: () => setInput(original) },
        })
      } else {
        toast.info('Nothing to improve — your prompt was kept as-is.')
      }
    } catch {
      toast.error('Could not enhance the prompt — your text is unchanged.')
    } finally {
      setEnhancing(false)
    }
  }

  /**
   * Send `text` as if the user had typed it into the composer and hit send — the
   * one routing decision for free-form input. Follow-up chips go through here
   * too: a follow-up is a NEW message, not an answer to a parked clarification.
   * Routing it to `submitClarificationAnswer` made it call `resumeRun` on a
   * FINISHED run, which the backend rejects with a 400 once the status is no
   * longer `waiting_for_user` (Khang on #27046).
   */
  const sendText = async (text: string) => {
    if (!text || busy) return
    // Routing priority: an ACTIVE generation question owns typed text — it is
    // the most recent thing the assistant asked. Only then does a parked run's
    // clarification get the text (a loaded run can sit in waiting_for_user
    // while the user is authoring a NEW workflow; answering the old run with
    // fresh gen-question input was the "custom input doesn't work" bug).
    if (genSessionRef.current) {
      await answerGenQuestion(text)
      return
    }
    if (awaitingClarification) {
      await submitClarificationAnswer(text)
      return
    }
    // A plain run command starts the workflow (like the Run pill) instead of
    // being fed into generation. Only when there IS something runnable.
    const canRunNow =
      Boolean(workflow.planPreview || workflow.openedTemplateId) && !runInFlight
    if (isRunCommand(text) && canRunNow) {
      setBlocks((prev) => [
        ...prev,
        { id: nextChatId(), kind: 'user', text, at: new Date().toISOString() },
        { id: nextChatId(), kind: 'assistant', text: STARTING_RUN_LINE },
      ])
      const started = await workflow.startRun()
      if (!started) setBlocks((prev) => dropTrailingStarting(prev))
      return
    }
    // A run command with nothing runnable used to fall through to generation.
    // "run the workflow" is not a question — drafting it would put a nonsense plan
    // on the canvas. Ask for the question instead.
    if (isRunCommand(text)) {
      setBlocks((prev) => [
        ...prev,
        { id: nextChatId(), kind: 'user', text, at: new Date().toISOString() },
        {
          id: nextChatId(),
          kind: 'assistant',
          text: runInFlight
            ? RUN_ALREADY_GOING
            : 'Tell me what to find out and I will draft a workflow, e.g. "Did the reboiler level loop hold its set point last quarter?"',
        },
      ])
      return
    }
    // Premade templates carry [bracketed] placeholders the user must fill —
    // block generation until they do (an unfilled target can never run).
    const placeholders = unfilledPlaceholders(text)
    if (placeholders.length > 0) {
      setInput(text) // give the text back for editing
      setBlocks((prev) => [
        ...prev,
        {
          id: nextChatId(),
          kind: 'assistant',
          text: `Please replace ${placeholders.join(', ')} with your actual equipment, tags, or values before sending — I can't build a runnable workflow around placeholders.`,
        },
      ])
      return
    }
    setBlocks((prev) => [
      ...prev,
      { id: nextChatId(), kind: 'user', text, at: new Date().toISOString() },
    ])
    await generateFromPrompt(text)
  }

  /**
   * A typed question DRAFTS THE PLAN — under both engines.
   *
   * #29950 skipped this under v2 because the v1 generator's plan was discarded (~109 s of work
   * that never ran). The generate-draft route now returns the v2 preview (matched blueprint,
   * unmatched DAG, or sequenced template fallback), so the planning checklist and the "Run
   * workflow" confirmation are back. Run echoes `requestedPlan` and unmatched `compiledSteps`
   * so those cards execute.
   *
   * A send while a run is already live stays ordinary chat — it does not start a second run
   * and does not replace the live plan.
   */
  const generateFromPrompt = async (text: string) => {
    if (runInFlight) {
      setBlocks((prev) => [
        ...prev,
        { id: nextChatId(), kind: 'assistant', text: RUN_ALREADY_GOING },
      ])
      return
    }
    await draftFromPrompt(text)
  }

  /**
   * THE AUTHORING TAIL — the Planning checklist, then the canvas, then "Run workflow".
   *
   * Under v1 the drawn plan is what the run executes. Under v2 the generate-draft route
   * returns the engine's preview (matched blueprint, unmatched DAG, or sequenced template
   * fallback). Run echoes `requestedPlan` and unmatched `compiledSteps` so those cards execute.
   */
  const draftFromPrompt = async (text: string) => {
    // Captured BEFORE the call: an authored draft already on screen means this turn is a
    // FOLLOW-UP, whose text is a question about the workflow and must not become the
    // workflow's instructions (Jenna, feedback item 15). A canonical fallback from a failed
    // first generate is not authored — retrying it is still the initial authoring request.
    const hadDraftBeforeSend = workflow.draftIsAuthored
    workflow.setWorkflowPrompt(text)
    // The chat description doubles as the run's problem statement (the old
    // Configure form that carried `problem` was replaced by Settings).
    workflow.setProblem(text)
    // Passed explicitly — `setWorkflowPrompt` above has not flushed, so `generateDraft` would
    // otherwise read a stale (empty) prompt and route to the canonical template instead of
    // generating from what was typed. (#29950 removed the Extended Thinking toggle that used to
    // augment this string; the prompt goes out as typed.)
    const outgoing = text
    const takeDraft = (forceGenerate = false) =>
      runWithPlanningStages(
        [
          { label: PLANNING_LABELS.understanding },
          { label: PLANNING_LABELS.drafting },
          { label: PLANNING_LABELS.hitl },
          { label: PLANNING_LABELS.validating },
        ],
        // TWO ARGS WHEN THERE IS NOTHING TO SAY, not a third one holding `undefined`.
        // `toHaveBeenCalledWith('workflow_chat', outgoing)` — which six sibling cases assert —
        // counts an explicit trailing `undefined` as a third argument and fails, and the call
        // shape those cases pin is the one every other caller here uses.
        () =>
          forceGenerate
            ? workflow.generateDraft('workflow_chat', outgoing, { forceGenerate: true })
            : workflow.generateDraft('workflow_chat', outgoing),
        (result) =>
          result.generated &&
          !(result.questions && result.questions.length > 0) &&
          !result.templateSuggestion
      )
    let { draft, generated, questions, templateSuggestion } = await takeDraft()
    // Create-workflow already chose a fresh start. A tenant reuse card would
    // reopen the last conversation for the same question.
    const blankBuilder = !workflow.openedTemplateId
    if (blankBuilder && templateSuggestion?.source === 'tenant') {
      const tenantOnly = !(questions && questions.length > 0) && !generated
      if (tenantOnly) {
        ;({ draft, generated, questions, templateSuggestion } = await takeDraft(true))
      }
      if (templateSuggestion?.source === 'tenant') templateSuggestion = undefined
    }
    // Cursor-style ask: the generator wants answers before drafting — walk
    // the questions one card at a time (Figma), then re-generate with the trail.
    if ((questions && questions.length > 0) || templateSuggestion) {
      pushGenQuestions(questions ?? [], outgoing, templateSuggestion)
      return
    }
    // A draft already on screen ⇒ this turn is a FOLLOW-UP, so its text is a question about
    // the workflow, not the workflow's instructions.
    pushGenerationOutcome(draft, generated, text, hadDraftBeforeSend)
  }

  /** Composer submit: what the user typed, cleared from the box, then routed. */
  const send = async () => {
    const text = input.trim()
    if (!text || busy) return
    setInput('')
    await sendText(text)
  }

  // Inline edit (pencil on a user message): rewrite that message IN PLACE,
  // drop everything after it (the edit rewrites history from that point —
  // stale question cards or a superseded draft summary must not linger), and
  // regenerate from the edited text.
  const resendEdited = async (blockId: string, text: string) => {
    const trimmed = text.trim()
    if (!trimmed || busy) return
    genSessionRef.current = null
    setBlocks((prev) => {
      const idx = prev.findIndex((b) => b.id === blockId)
      if (idx === -1) return prev
      const edited = { ...prev[idx], text: trimmed, at: new Date().toISOString() } as ChatBlock
      return [...prev.slice(0, idx), edited]
    })
    await generateFromPrompt(trimmed)
  }

  // The ACTIVE ask (generation questions / run clarification) docks directly
  // above the composer — attached to the input — while everything behind it
  // (planning included, in its own place) keeps scrolling. Once answered, the
  // card returns to the transcript flow as history.
  // Matched from the END: a run can legitimately ask the same question text
  // at two different checkpoints — the LIVE ask is always the newest block,
  // and first-match would re-pin the older, already-answered card instead.
  const dockedBlock =
    [...blocks].reverse().find((block) => {
      if (block.kind === 'gen_questions') return block.id === genSessionRef.current?.blockId
      if (block.kind === 'clarification') {
        return (
          workflow.run?.status === 'waiting_for_user' &&
          workflow.clarificationQuestion.trim() === block.question.trim()
        )
      }
      return false
    }) ?? null

  return (
    <div className="flex h-full flex-col">
      <div
        ref={scrollRef}
        onScroll={handleTranscriptScroll}
        // [&>*]:shrink-0 — chat items keep their natural height so a long
        // transcript SCROLLS instead of squishing every card into a thin line.
        className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-4 [&>*]:shrink-0"
      >
        {/* Scroll-back across runs: a Load-earlier affordance, then the lazily
            fetched older-run transcripts (read-only), oldest at the very top. */}
        {hasOlderRuns &&
          (historyExhausted ? (
            historySegments.length > 0 && (
              <p className="py-1 text-center text-[11px] text-muted-foreground">
                Start of history
              </p>
            )
          ) : (
            <button
              type="button"
              onClick={() => void loadOlderRun()}
              disabled={historyLoading}
              className="mx-auto flex items-center gap-1.5 rounded-md px-3 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted/60 disabled:opacity-60"
            >
              {historyLoading ? (
                <Loader2 className="size-3 animate-spin" />
              ) : (
                <ArrowUp className="size-3" />
              )}
              {historyLoading ? 'Loading earlier runs…' : 'Load earlier runs'}
            </button>
          ))}
        {historySegments.map((seg) => (
          <Fragment key={seg.runId}>
            <RunHistoryDivider label={seg.label} />
            {seg.blocks
              .filter((block) => block.kind !== 'step' && block.kind !== 'seed')
              // Our own retired planning line is not drawn — see `isRetiredPlanningLine`.
              .filter((block) => !isRetiredPlanningLine(block))
              .map((block) => (
                <WorkflowChatBlock
                  key={`${seg.runId}:${block.id}`}
                  block={block}
                  workflow={workflow}
                  busy={false}
                  onAnswer={noop}
                  onGenAnswer={noop}
                  onGenNavigate={noop}
                  activeGenBlockId={null}
                  onEditSubmit={noop}
                  onSend={noop}
                />
              ))}
          </Fragment>
        ))}
        {historySegments.length > 0 && <RunHistoryDivider label="Current run" />}
        <WorkflowMessages workflow={workflow} />
        {blocks
          .filter((block) => block.kind !== 'step' && block.id !== dockedBlock?.id)
          // Our own retired planning line is not drawn — see `isRetiredPlanningLine`. Dropped
          // here rather than on hydration so opening an old run does not rewrite its stored log.
          .filter((block) => !isRetiredPlanningLine(block))
          // The seed card is an EMPTY state: "Build your workflow. Use chat to describe your
          // workflow" sitting above the conversation it was asking for (reported by Jenna,
          // feedback doc issue 8). It goes the moment anything else exists to show.
          .filter((block) => block.kind !== 'seed' || blocks.length === 1)
          .map((block) => (
            <WorkflowChatBlock
              key={block.id}
              block={block}
              workflow={workflow}
              busy={busy}
              onAnswer={submitClarificationAnswer}
              onGenAnswer={answerGenQuestion}
              onGenNavigate={navigateGenQuestion}
              onEditSubmit={resendEdited}
              onSend={sendText}
              activeGenBlockId={genSessionRef.current?.blockId ?? null}
              runInFlight={runInFlight}
              live
              onFocusComposer={() => {
                // Focus alone can land off-screen on a long transcript — the
                // caret moves somewhere the user cannot see.
                // Optional-called: jsdom does not implement scrollIntoView, and a
                // focus affordance must not throw in a test environment.
                composerRef.current?.scrollIntoView?.({ block: 'nearest' })
                composerRef.current?.focus()
              }}
              onViewReport={onViewReport}
              onSupplyDocument={supplyMethodDocument}
              onPickInterview={() => setInterviewPickOpen(true)}
              onRequestCall={requestMethodCall}
              onSubmitMeetLink={submitMeetLink}
            />
          ))}
        {/* Animated agent indicators (Figma): orange "Planning" while the
            draft generates; "Thinking..." while a run executes — the per-step
            detail streams onto the CANVAS nodes, not into the chat. */}
        {/* The Planning/Generating checklist STAYS — it is the honest signal that work is
            happening, and progress so far should not vanish while the user answers a
            question. What must not linger is the previous workflow's step CHART on the
            canvas (see WorkflowBuilder: an empty graph while generating). Hiding the
            indicator instead was the wrong half of FS item 1. */}
        {(workflow.loading || planningStages.length > 0) && (
          <PlanningIndicator stages={planningStages} paused={planningPaused} />
        )}
        {runInFlight && <ThinkingIndicator />}
        {/* Parked-run gates: without these controls an awaiting_approval or
            paused run was unadvanceable from the product (the endpoints
            existed; the buttons did not). */}
        {workflow.run?.status === 'awaiting_approval' && (
          <div className="rounded-xl border border-border bg-background p-3.5 dark:bg-dark-card">
            <p className="text-sm font-medium text-foreground">Ready to execute</p>
            <p className="mt-0.5 text-sm text-muted-foreground">
              This run is waiting for your approval before any step executes.
            </p>
            <IntuiButton
              className="mt-2.5"
              size="sm"
              loading={workflow.resuming}
              onClick={() => void workflow.approveRun()}
            >
              Approve & start
            </IntuiButton>
          </div>
        )}
        {failedRunRetry && (
          <div className="flex items-center gap-2">
            <IntuiButton
              size="sm"
              variant="outline"
              loading={workflow.resuming}
              onClick={() => void workflow.retryRun()}
            >
              {failedRunRetry.button}
            </IntuiButton>
            <span className="text-xs text-muted-foreground">{failedRunRetry.hint}</span>
          </div>
        )}
        {workflow.run?.status === 'paused' && (
          <div className="rounded-xl border border-border bg-background p-3.5 dark:bg-dark-card">
            <p className="text-sm font-medium text-foreground">Run paused</p>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Execution is paused; nothing runs until you resume.
            </p>
            <IntuiButton
              className="mt-2.5"
              size="sm"
              variant="outline"
              loading={workflow.resuming}
              onClick={() => void workflow.unpauseRun()}
            >
              Resume run
            </IntuiButton>
          </div>
        )}
      </div>

      {/* The active ask, pinned above the input (outside the scroll area).
          Height-capped + internally scrollable: a tall clarification card is
          shrink-0, so without the cap it consumed the whole panel and
          collapsed the flex-1 transcript above to ~0 — the background then
          looked "not scrollable" when a clarification question appeared
          (Pegah, workflow feedback 2026-07-14). The cap keeps the transcript
          its own scrollable height. */}
      {dockedBlock && (
        /* `data-testid` so a test can assert WHICH copy rendered the routes. Both mounts draw the
           active ask, so a panel-level query for a route hint passes when only the scrolled copy
           has the handler — which is the exact defect below, invisible to the test that covers it
           (claude review on #30871). */
        <div data-testid="docked-ask" className="max-h-[45%] shrink-0 overflow-y-auto px-3 pb-2">
          <WorkflowChatBlock
            block={dockedBlock}
            workflow={workflow}
            busy={busy}
            onAnswer={submitClarificationAnswer}
            onGenAnswer={answerGenQuestion}
            onGenNavigate={navigateGenQuestion}
            onEditSubmit={resendEdited}
            onSend={sendText}
            activeGenBlockId={genSessionRef.current?.blockId ?? null}
            /* THE INTAKE'S THREE ROUTES ARE ACTIONS, and this is the card the reader is looking at.
               `ClarificationCard` draws the document picker only when it HAS a handler
               (`offersDocument = Boolean(onSupplyDocument) && options.includes(SEND_A_DOCUMENT)`);
               the docked copy of the active ask was mounted without one, so on the method intake
               the three actions fell through to the generic candidate-answer rendering — selectable
               rows, a "Continue (3)" that posts "Answer these questions, Send a document, Get on a
               call" as the ANSWER, and no way to upload anything. Suite 2's whole beat is that ask
               leading to an upload, and the upload could not be started from it. The scrolled list
               above has had these three since the routes were built; the docked card is the one a
               reader actually clicks.

               BOTH COPIES ARE NOW CLICKABLE, which is worth knowing rather than fixing: the active
               ask renders twice by design, so the picker can be opened from either. There is no
               divergent state — `supplyMethodDocument` is the same handler in both mounts, and the
               run is what the upload posts to (review on #30871). */
            onSupplyDocument={supplyMethodDocument}
            onPickInterview={() => setInterviewPickOpen(true)}
            onRequestCall={requestMethodCall}
          />
        </div>
      )}

      <div className="shrink-0 p-3 pt-0">
        {/* Premade prompt chips — only while the chat has no real context yet. */}
        {showPremade && (
          <div className="mb-2 flex items-center gap-2 overflow-x-auto pb-0.5">
            {PREMADE_PROMPTS.map((premade) => {
              const selected = input === premade.prompt
              return (
                <button
                  key={premade.label}
                  type="button"
                  onClick={() => setInput(premade.prompt)}
                  className={cn(
                    'shrink-0 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors',
                    selected
                      ? 'border-transparent bg-foreground text-background'
                      : 'border-border bg-background text-foreground hover:bg-muted dark:bg-dark-card'
                  )}
                >
                  {premade.label}
                </button>
              )
            })}
          </div>
        )}

        <div className="flex flex-col gap-2 rounded-xl border border-input bg-background p-2 dark:bg-dark-card">
          {/* Attached-file chips: 2 max, then "+N more" opens the full list. */}
          {attachedFiles.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              {attachedFiles.slice(0, 2).map((entry, index) => {
                const name = knowledgeFileName(entry)
                return (
                  <span
                    // Index keys: entries can legally repeat a name (bare
                    // string + {name} object) and removal is positional.
                    key={`${name}-${index}`}
                    title={name}
                    className="inline-flex max-w-[200px] items-center gap-1.5 rounded-md border border-border bg-muted/40 px-2 py-1 text-xs text-foreground"
                  >
                    <FileText className="size-3.5 shrink-0 text-muted-foreground" />
                    <span className="min-w-0 truncate">{name}</span>
                    <button
                      type="button"
                      aria-label={`Remove ${name}`}
                      onClick={() => removeAttachedFile(index)}
                      className="grid size-4 shrink-0 place-items-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
                    >
                      <X className="size-3" />
                    </button>
                  </span>
                )
              })}
              {attachedFiles.length > 2 && (
                <button
                  type="button"
                  onClick={() => setFilesModalOpen(true)}
                  className="inline-flex items-center rounded-md border border-border px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                >
                  +{attachedFiles.length - 2} more
                </button>
              )}
            </div>
          )}
          <IntuiTextarea
            ref={composerRef}
            // Stable handle for tests: the PLACEHOLDER is state-dependent now (it is the
            // next-action cue), so selecting the composer by its copy would couple every
            // test to whichever state the fixture happens to be in.
            data-testid="workflow-composer"
            value={input}
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault()
                void send()
              }
            }}
            minRows={2}
            maxRows={8}
            autoResize
            placeholder={composerPlaceholder}
            // Smaller type + tighter padding so more of the conversation is visible
            // at once (Putra, 2026-08-03: "smaller text and padding to cater more context").
            className="border-0 px-2 py-1.5 text-[13px] leading-snug shadow-none focus-visible:border-0 focus-visible:shadow-none"
          />
          <div className="flex items-center justify-between gap-2">
            {/* "+" menu: Attach File · Enhance Prompt · Evidence verification */}
            <IntuiDropdownMenu>
              <IntuiDropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label="More composer options"
                  className="grid size-8 place-items-center rounded-lg border border-border text-foreground transition-colors hover:bg-muted"
                >
                  <Plus className="size-4" />
                </button>
              </IntuiDropdownMenuTrigger>
              <IntuiDropdownMenuContent align="start" side="top">
                <IntuiDropdownMenuItem onSelect={() => onAttachFile?.()}>
                  <FilePlus2 className="size-4" />
                  Attach File
                </IntuiDropdownMenuItem>
                <IntuiDropdownMenuItem
                  disabled={enhancing || !input.trim()}
                  onSelect={() => void enhancePrompt()}
                >
                  {enhancing ? (
                    <Loader2 className="size-4 animate-spin" aria-hidden />
                  ) : (
                    <Sparkles className="size-4" />
                  )}
                  Enhance Prompt
                </IntuiDropdownMenuItem>
                {/* Run-time toggle (distinct from Extended Thinking, which only
                    augments the generation prompt): appends an adversarial
                    verify-diagnosis gate to a troubleshooting run. Only the base
                    troubleshooting flow composes that gate (backend
                    composeVerification is scoped to troubleshooting_base), so
                    hide the item on an opened METHOD template (HAZOP/FMEA/…),
                    where it would read active yet be a backend no-op (rahul8436
                    review). Default compose (nothing opened) IS the base flow. */}
                {isVerificationApplicable(
                  workflow.run?.templateId ?? workflow.openedTemplateId,
                ) && (
                  <IntuiDropdownMenuItem onSelect={() => workflow.setVerification((v) => !v)}>
                    <ShieldCheck className="size-4" />
                    Evidence verification
                    {workflow.verification && (
                      <span className="ml-auto text-xs text-cyan-700">On</span>
                    )}
                  </IntuiDropdownMenuItem>
                )}
              </IntuiDropdownMenuContent>
            </IntuiDropdownMenu>

            <div className="flex items-center gap-1.5">
              <button
                type="button"
                disabled
                aria-label="Voice input (coming soon)"
                className="grid size-8 place-items-center rounded-lg text-muted-foreground opacity-50"
              >
                <Mic className="size-4" />
              </button>
              {busy ? (
                <button
                  type="button"
                  onClick={() => void workflow.cancelRun()}
                  disabled={!cancellableRun}
                  aria-label={cancellableRun ? 'Stop the run' : 'Working…'}
                  className="grid size-8 place-items-center rounded-lg bg-brand text-white transition-opacity disabled:opacity-70"
                >
                  <Square className="size-3.5 fill-current" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => void send()}
                  disabled={!input.trim()}
                  aria-label={
                    awaitingClarification
                      ? 'Send clarification answer'
                      : 'Send workflow prompt'
                  }
                  className="grid size-8 place-items-center rounded-lg bg-brand text-white transition-opacity disabled:opacity-50"
                >
                  <ArrowUp className="size-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* "Use a past interview": the catalog picker locked to transcripts. Rendered here
          rather than in the builder so the chat echo and the outcome message stay with the
          other supply-a-method flows.

          Mounted only WHILE OPEN. `useFileUpload` constructs a real Uppy on mount — global
          online/offline listeners plus a periodic status timer — so an always-mounted dialog
          booted one on EVERY panel render, for a picker the user may never open. In tests that
          timer outlives the jsdom teardown and throws `window is not defined` as an unhandled
          error, which Vitest fails the whole run on. Nothing here is needed before it opens. */}
      {interviewPickOpen && (
        <SelectFilesDialog
          open={interviewPickOpen}
          lockedType="transcript"
          onClose={() => setInterviewPickOpen(false)}
          onSelect={(files) => {
            const picks = files.filter((file) => file.documentId)
            const picked = picks[0]
            if (!picked?.documentId) return
            // A method is ONE document. The catalog picker is multi-select, so say plainly which
            // one was used instead of dropping the rest in silence (greptile P2 on #29842).
            if (picks.length > 1) {
              setBlocks((prev) => [
                ...prev,
                {
                  id: nextChatId(),
                  kind: 'assistant',
                  text: `A method comes from one interview, so I used "${picked.name}". Pick again if you meant a different one.`,
                },
              ])
            }
              void supplyMethodInterview(picked.documentId, picked.name)
            }}
        />
      )}

      {/* Full attached-files list ("+N more"): every file on this workflow,
          each removable — the chips only preview the first two. */}
      <Dialog open={filesModalOpen} onOpenChange={setFilesModalOpen}>
        <DialogContent className="max-w-md">
          <DialogTitle>Attached files ({attachedFiles.length})</DialogTitle>
          <ul className="flex max-h-80 flex-col gap-1 overflow-y-auto">
            {attachedFiles.map((entry, index) => {
              const name = knowledgeFileName(entry)
              return (
                <li
                  key={`${name}-${index}`}
                  className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm"
                >
                  <FileText className="size-4 shrink-0 text-muted-foreground" />
                  <span className="min-w-0 flex-1 truncate text-foreground" title={name}>
                    {name}
                  </span>
                  <button
                    type="button"
                    aria-label={`Remove ${name}`}
                    onClick={() => removeAttachedFile(index)}
                    className="grid size-6 shrink-0 place-items-center rounded text-muted-foreground hover:bg-muted hover:text-foreground"
                  >
                    <X className="size-3.5" />
                  </button>
                </li>
              )
            })}
          </ul>
        </DialogContent>
      </Dialog>
    </div>
  )
}

/** Round avatar for a chat row: the agent (bot) on the left, the user on the right. */
/**
 * A user message (Figma "3. Create Workflow"): a full-width muted card —
 * left-aligned, no avatar — with time · copy · edit chrome BELOW it. The
 * pencil edits the message INLINE (textarea in the card's place); saving
 * rewrites history from that message and regenerates.
 */
function UserMessageBlock({
  block,
  busy,
  onEditSubmit,
}: {
  block: Extract<ChatBlock, { kind: 'user' }>
  busy: boolean
  onEditSubmit: (blockId: string, text: string) => void | Promise<void>
}) {
  const [editing, setEditing] = useState(false)
  const [text, setText] = useState(block.text)

  if (editing) {
    return (
      <div className="rounded-xl border border-border bg-background p-2.5 dark:bg-dark-card">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={Math.min(6, Math.max(2, text.split('\n').length))}
          autoFocus
          aria-label="Edit message"
          className="w-full resize-y rounded-md bg-transparent px-1.5 py-1 text-sm leading-relaxed text-foreground outline-none"
        />
        <div className="mt-2 flex items-center justify-end gap-2">
          <IntuiButton
            variant="outline"
            size="sm"
            onClick={() => {
              setText(block.text)
              setEditing(false)
            }}
          >
            Cancel
          </IntuiButton>
          <IntuiButton
            size="sm"
            disabled={!text.trim() || busy}
            onClick={() => {
              setEditing(false)
              void onEditSubmit(block.id, text)
            }}
          >
            Save & regenerate
          </IntuiButton>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="whitespace-pre-wrap rounded-xl bg-muted px-4 py-3 text-sm leading-relaxed text-foreground dark:bg-dark-muted">
        {block.text}
      </div>
      {/* Chrome below the card (Figma): time · copy · edit. */}
      <div className="mt-1.5 flex items-center justify-end gap-2 text-xs text-muted-foreground">
        {block.at && (
          <span>
            {new Date(block.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
        )}
        <button
          type="button"
          aria-label="Copy message"
          onClick={() =>
            void navigator.clipboard?.writeText(block.text).catch(() => {
              toast.error('Could not copy to the clipboard')
            })
          }
          className="hover:text-foreground"
        >
          <Copy className="size-3.5" />
        </button>
        <button
          type="button"
          aria-label="Edit message"
          disabled={busy}
          onClick={() => {
            setText(block.text)
            setEditing(true)
          }}
          className="hover:text-foreground disabled:opacity-50"
        >
          <Pencil className="size-3.5" />
        </button>
      </div>
    </div>
  )
}

/**
 * Cursor-style generation ask (Figma 8-edge-cases "Multiple/Single
 * Confirmation"): ONE card for all questions — question text top-left,
 * `< n of N >` chevrons top-right (only when N > 1), option rows with a small
 * description, and a Custom row that expands into an INLINE input right in
 * the card (typing in the main composer still works as an alternative).
 * Once every question is answered the card collapses into the
 * question → answer summary (the "Answered" screen).
 */
function GenQuestionsCard({
  block,
  busy,
  active,
  onAnswer,
  onNavigate,
}: {
  block: Extract<ChatBlock, { kind: 'gen_questions' }>
  busy: boolean
  active: boolean
  onAnswer: (text: string) => void | Promise<void>
  onNavigate: (delta: number) => void
}) {
  const total = block.questions.length
  const [customOpen, setCustomOpen] = useState(false)
  const [customText, setCustomText] = useState('')
  // Moving to another question closes the input — each question's custom
  // answer starts fresh.
  useEffect(() => {
    setCustomOpen(false)
    setCustomText('')
  }, [block.index])
  const submitCustom = () => {
    const text = customText.trim()
    if (!text || busy) return
    setCustomOpen(false)
    setCustomText('')
    void onAnswer(text)
  }
  if (block.completed || !active) {
    // Summary treatment (Figma "Answered"): each question is its own bordered
    // card directly in the flow — question + chosen answer below. Index keys:
    // the generator can legally repeat the same question text.
    return (
      <div className="flex flex-col gap-2">
        {block.questions.map((question, i) => (
          <div key={i} className="rounded-xl border border-border bg-background px-4 py-3 dark:bg-dark-card">
            <p className="text-sm font-medium text-foreground">{question.question}</p>
            <p className="mt-0.5 text-sm text-muted-foreground">{block.answers[i] ?? '—'}</p>
          </div>
        ))}
      </div>
    )
  }
  const question = block.questions[block.index]
  const chosen = block.answers[block.index]
  return (
    <div className="rounded-xl border border-border bg-background p-3.5 dark:bg-dark-card">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm font-medium text-foreground">{question.question}</p>
        {total > 1 && (
          <span className="flex shrink-0 items-center gap-1 pt-0.5 text-xs text-muted-foreground">
            <button
              type="button"
              aria-label="Previous question"
              disabled={block.index === 0}
              onClick={() => onNavigate(-1)}
              className="grid size-4 place-items-center hover:text-foreground disabled:opacity-40"
            >
              <ChevronLeft className="size-4" />
            </button>
            {block.index + 1} of {total}
            <button
              type="button"
              aria-label="Next question"
              disabled={block.index === total - 1}
              onClick={() => onNavigate(1)}
              className="grid size-4 place-items-center hover:text-foreground disabled:opacity-40"
            >
              <ChevronRight className="size-4" />
            </button>
          </span>
        )}
      </div>
      <div className="mt-3 flex flex-col gap-1.5">
        {question.options.map((option) => {
          const selected = chosen === option.label
          return (
            <button
              key={option.label}
              type="button"
              disabled={busy}
              onClick={() => void onAnswer(option.label)}
              className={cn(
                'rounded-lg border px-3 py-2 text-left transition-colors',
                selected
                  ? 'border-cyan-400 bg-cyan-50/50 dark:border-cyan-700 dark:bg-cyan-950/30'
                  : 'border-border hover:bg-muted disabled:opacity-60 disabled:hover:bg-transparent'
              )}
            >
              <span className="block text-sm font-medium text-foreground">{option.label}</span>
              {option.description && (
                <span className="block text-xs text-muted-foreground">{option.description}</span>
              )}
            </button>
          )
        })}
        {question.allowCustom &&
          (customOpen ? (
            <div className="flex items-center gap-1.5 rounded-lg border border-cyan-400 p-1.5 dark:border-cyan-700">
              <input
                autoFocus
                value={customText}
                onChange={(event) => setCustomText(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault()
                    submitCustom()
                  }
                  if (event.key === 'Escape') {
                    setCustomOpen(false)
                    setCustomText('')
                  }
                }}
                placeholder="Type your answer..."
                aria-label="Custom answer"
                className="min-w-0 flex-1 bg-transparent px-1.5 py-1 text-sm text-foreground outline-none placeholder:text-muted-foreground"
              />
              <button
                type="button"
                aria-label="Submit custom answer"
                disabled={busy || !customText.trim()}
                onClick={submitCustom}
                className="grid size-7 shrink-0 place-items-center rounded-md bg-cyan-700 text-white transition-opacity disabled:opacity-40"
              >
                <ArrowUp className="size-4" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              disabled={busy}
              onClick={() => setCustomOpen(true)}
              className="rounded-lg border border-border px-3 py-2 text-left text-sm font-medium text-foreground transition-colors hover:bg-muted disabled:opacity-60"
            >
              Custom
            </button>
          ))}
      </div>
    </div>
  )
}

/**
 * A generation-time clarifying question (Figma chat flow: "Which maintenance
 * history range should we use for FP-101?"): stacked option buttons with
 * label + description, answered one card at a time; typing in the composer is
 * always the Custom escape. Answered/restored cards render inert with the
 * chosen answer highlighted.
 */
function GenQuestionCard({
  block,
  busy,
  onAnswer,
}: {
  block: Extract<ChatBlock, { kind: 'gen_question' }>
  busy: boolean
  onAnswer: (text: string) => void | Promise<void>
}) {
  const answered = block.answered !== undefined
  return (
    <div className="rounded-xl border border-border bg-background p-3.5 dark:bg-dark-card">
      <p className="text-sm font-medium text-foreground">{block.question}</p>
      <div className="mt-3 flex flex-col gap-1.5">
        {block.options.map((option) => {
          const chosen = answered && block.answered === option.label
          return (
            <button
              key={option.label}
              type="button"
              disabled={busy || answered}
              onClick={() => void onAnswer(option.label)}
              className={cn(
                'rounded-lg border px-3 py-2 text-left transition-colors',
                chosen
                  ? 'border-cyan-400 bg-cyan-50/50 dark:border-cyan-700 dark:bg-cyan-950/30'
                  : 'border-border hover:bg-muted disabled:opacity-60 disabled:hover:bg-transparent'
              )}
            >
              <span className="block text-sm font-medium text-foreground">{option.label}</span>
              {option.description && (
                <span className="block text-xs text-muted-foreground">{option.description}</span>
              )}
            </button>
          )
        })}
        {block.allowCustom && !answered && (
          // A hint, not an action: the custom answer is typed in the composer.
          <span className="rounded-lg border border-dashed border-border px-3 py-2 text-left text-sm text-muted-foreground">
            Custom — type your answer below
          </span>
        )}
      </div>
      {!answered && block.total > 1 && (
        <p className="mt-2 text-xs text-muted-foreground">
          Question {block.index + 1} of {block.total}
        </p>
      )}
    </div>
  )
}

/**
 * A clarification / HITL gate rendered as a confirmation card (Figma via-chat
 * screen 8): the agent's question + the candidate answers it proposed as a
 * selectable list (prune with ×, re-add with +), confirmed as a set via
 * "Continue (N)" or rejected via "None fit". The intake's options are ACTIONS
 * rather than candidate answers, so that list is not rendered there — each
 * action is its own button (document picker / Get on a call / Answer these
 * questions). Free-text in the composer is always an option.
 * an option. Plain questions (no candidates) render question + the free-text
 * hint only. Only the active (latest unanswered) clarification is interactive;
 * historical ones render read-only.
 */
export function ClarificationCard({
  question,
  options,
  isActive,
  busy,
  checkpoint = false,
  resolvedSources,
  onAnswer,
  onSupplyDocument,
  onPickInterview,
  onRequestCall,
  askRole,
}: {
  question: string
  options: string[]
  isActive: boolean
  busy: boolean
  /**
   * Is this park asking for the METHOD or for DATA — read off the park by the hook's
   * `suppliedRole`, the same read that labels the artifact card afterwards (#31304).
   *
   * A data ask must not wear method clothes. An escalation asking for downtime records offered
   * "Attach a file that describes the process", "or use a past interview" and "describe the method
   * below" (dev runs `3819cee5` / `c74ba9a5`) — every one of those names the one lane the file is
   * not taking. Absent ⇒ method, which is the wording every park carried before this field.
   */
  askRole?: 'method' | 'data'
  /** HITL review gate (step done, review before continuing) vs a real question. */
  checkpoint?: boolean
  /** What the run's triage resolved — turns a bare gate into the sources check. */
  resolvedSources?: WorkflowResolvedSources | null
  onAnswer: (text: string) => void | Promise<void>
  /**
   * Supply a METHOD DOCUMENT instead of typing the method.
   *
   * Present only on the intake gate, which is the one clarification whose options are ACTIONS
   * ("Answer these questions / Send a document / Get on a call") rather than candidate answers to
   * prune. Until this existed the button posted its own label, the backend discarded it as a
   * choice rather than an answer, and the run re-parked on the same question.
   */
  onSupplyDocument?: (file: File) => void | Promise<void>
  /**
   * Open the past-interview picker. Separate from `onSupplyDocument` because the source is a
   * catalog transcript, not a local file, and the run records it as an interview.
   */
  onPickInterview?: () => void
  /** Start the "get on a call" exchange (asks for the meeting link). */
  onRequestCall?: () => void

}) {
  // Agent proposes candidates; default to all selected, let the user prune.
  // Initialised once per mount — relies on each clarification being a distinct
  // chat block (WorkflowChatBlock is keyed by block.id), so a new clarification
  // mounts a fresh ClarificationCard rather than carrying over a stale selection.
  const [selected, setSelected] = useState<string[]>(options)
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const hasOptions = options.length > 0
  // The intake offers a document; every other clarification's options are candidate answers.
  const offersDocument = Boolean(onSupplyDocument) && options.includes(SEND_A_DOCUMENT)
  /**
   * THE CARD'S OWN ANSWERS — every option that is not one of the three fixed intake routes.
   *
   * Rendered INDEPENDENTLY of `offersDocument`, which is the fix: these buttons used to live inside
   * that branch, and `offersDocument` requires the card to carry the exact legacy `Send a document`
   * label. A planner-authored card offering `["Use the Kitimat plant", "Upload Sudbury's data"]`
   * carries no such label, so it never reached them — on the FIRST render, which is precisely the
   * render where the reader chooses between using this plant and sending their own data (greptile
   * P1 / review on #31166). It fell through to the multi-select list instead, where ticking both
   * posts `"Use the Kitimat plant, Upload Sudbury's data"` — a string no `supplyOption` equality
   * match can ever satisfy, on a card whose two answers are mutually exclusive.
   */
  const cardOptions = options.filter(
    (option) => !INTAKE_ROUTES.some((route) => route.option === option),
  )
  // Phrase options (spaces) are mutually exclusive actions and post verbatim (#31166).
  // Identifier candidates (COS1106E) keep the Figma confirmation set — Continue (N) / None fit.
  const exclusiveAnswers = cardOptions.some((option) => /\s/.test(option))
  const toggle = (option: string) =>
    setSelected((prev) => (prev.includes(option) ? prev.filter((o) => o !== option) : [...prev, option]))

  return (
    <div className="flex overflow-hidden rounded-xl border border-border bg-background dark:bg-dark-card">
        <div className="min-w-0 flex-1 p-4">
        <div className="mb-2 flex items-center gap-2">
          <span className="grid size-6 place-items-center rounded-md bg-muted text-cyan-700">
            <AlertCircle className="size-4" />
          </span>
          <span className="text-sm font-semibold text-foreground">
            {checkpoint ? 'Review checkpoint' : 'Clarification needed'}
          </span>
          {isActive && (
            <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-medium text-cyan-700 dark:text-cyan-500">
              <Loader2 className="size-3 animate-spin" aria-hidden />
              Waiting for you
            </span>
          )}
        </div>
        <p className="text-sm text-foreground">{question}</p>

        {hasOptions && !offersDocument && (
          <div className="mt-3 flex flex-col gap-1.5">
            {options.map((option) => {
              const isSelected = selected.includes(option)
              return (
                <div
                  key={option}
                  className={cn(
                    'flex items-center gap-2 rounded-lg border px-3 py-2 text-sm',
                    isSelected
                      ? 'border-cyan-300 bg-cyan-50/50 text-foreground dark:border-cyan-800 dark:bg-cyan-950/30'
                      : 'border-border text-muted-foreground'
                  )}
                >
                  <FileText className="size-4 shrink-0 text-muted-foreground" />
                  <span className={cn('min-w-0 flex-1 truncate', !isSelected && 'line-through')}>{option}</span>
                  {isActive && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => toggle(option)}
                      aria-label={isSelected ? `Remove ${option}` : `Add ${option}`}
                      className="grid size-5 shrink-0 place-items-center rounded text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
                    >
                      {isSelected ? <X className="size-3.5" /> : <Plus className="size-3.5" />}
                    </button>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {checkpoint && resolvedSources && <ResolvedSourcesGate sources={resolvedSources} />}

        {/* No options means the gate is a plain "may I proceed?" — and until now the
            ONLY way through was typing the literal word "continue". On the demo call the
            run halted repeatedly on it and the user looked for a button that did not
            exist (FS item 3). The composer stays available for "tell me what to change". */}
        {isActive && !hasOptions && checkpoint && (
          <div className="mt-3">
            <IntuiButton
              size="sm"
              disabled={busy}
              rightIcon={<ArrowRight />}
              onClick={() => void onAnswer('continue')}
            >
              Continue
            </IntuiButton>
          </div>
        )}

        {/* THE CARD'S OWN ANSWERS, when it brought some — see `cardOptions`.

            `INTAKE_ROUTES` is a fixed table of three (answer questions / send a document / get on
            a call), so a park offering anything else rendered NOTHING as a button and the reader
            had a free-text box under a question with two obvious answers. Since the planner
            started writing its own clarifications those labels are per-card, and only whoever
            wrote the card knows them. Each posts its label VERBATIM, which is what the backend
            matches `supplyOption` against by equality. */}
        {isActive && exclusiveAnswers && (
          <div className="mt-3 flex flex-col gap-2">
            {cardOptions.map((option) => (
              <button
                key={option}
                type="button"
                disabled={busy}
                onClick={() => void onAnswer(option)}
                className="rounded-lg border border-border px-3 py-2 text-left transition-colors hover:border-foreground/40 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <span className="block text-sm font-medium text-foreground">{option}</span>
              </button>
            ))}
          </div>
        )}

        {isActive && offersDocument && (
          <div className="mt-3 flex flex-col gap-2">
            {/* Figma 645:163089 — the intake is a stacked list of NAMED routes with a line of
                explanation each, not a row of bare chips. The labels below are the design's
                words; the MATCH stays on the backend's own option strings (`INTAKE_OPTIONS` in
                workflows-v2/elicit.ts), because that is what says these options are actions. */}
            <p className="text-sm text-foreground">To proceed, select either:</p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,.txt,.md"
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0]
                // Reset first: picking the SAME file twice fires no change event otherwise, so a
                // failed upload could not be retried without choosing a different document.
                event.target.value = ''
                if (file) void onSupplyDocument?.(file)
              }}
            />
            {INTAKE_ROUTES.filter((route) => options.includes(route.option)).map((route) => (
              <button
                key={route.option}
                type="button"
                disabled={busy}
                onClick={() => {
                  if (route.option === SEND_A_DOCUMENT) {
                    fileInputRef.current?.click()
                  } else if (route.option === GET_ON_A_CALL && onRequestCall) {
                    onRequestCall()
                  } else {
                    void onAnswer(route.option)
                  }
                }}
                className="rounded-lg border border-border px-3 py-2 text-left transition-colors hover:border-foreground/40 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <span className="block text-sm font-medium text-foreground">{route.label}</span>
                <span className="block text-xs text-muted-foreground">
                  {askRole === 'data' && route.option === SEND_A_DOCUMENT
                    ? 'Attach the data this run asked for'
                    : route.hint}
                </span>
              </button>
            ))}
            {/* NOT in Putra's frames — flagged for him. Picking an existing transcript is the
                route he argued for in Slack ("just pick from previous interviews from data
                catalog", 2026-08-21) and it needs no call placed, so it stays available but
                subordinate to the three drawn routes rather than becoming a fourth. */}
            {/* A PAST INTERVIEW IS A METHOD, never data. Offering it on a park that asked for a
                downtime export invites a transcript that cannot answer it. */}
            {onPickInterview && askRole !== 'data' && (
              <button
                type="button"
                disabled={busy}
                onClick={onPickInterview}
                className="self-start text-xs text-muted-foreground underline-offset-2 hover:underline disabled:opacity-50"
              >
                or use a past interview
              </button>
            )}
            <span className="text-xs text-muted-foreground">PDF, Word, text or Markdown</span>
          </div>
        )}

        {/* THE MULTI-SELECT is for a card whose options are CANDIDATE ANSWERS (several tags to
            choose among), not for one whose options are actions. A card carrying its own buttons
            gets those instead: ticking two of a mutually-exclusive pair posted them comma-joined,
            which no equality match can satisfy. */}
        {isActive && hasOptions && !offersDocument && !exclusiveAnswers && (
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              disabled={busy || selected.length === 0}
              onClick={() => void onAnswer(selected.join(', '))}
              className="inline-flex items-center rounded-lg bg-brand px-3 py-1.5 text-sm font-medium text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-50"
            >
              Continue{selected.length > 1 ? ` (${selected.length})` : ''}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => void onAnswer('None of the suggested options fit.')}
              className="inline-flex items-center rounded-lg border border-input bg-background px-3 py-1.5 text-sm text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
            >
              None fit
            </button>
          </div>
        )}

        {isActive && (
          <p className="mt-3 text-xs text-muted-foreground">
            {offersDocument
              ? askRole === 'data'
                ? 'Attach the file, or tell me below where this data lives.'
                : 'Send a document, get on a call, answer the questions, or describe the method below.'
              : hasOptions
              ? 'Confirm the ones that apply, or type your own answer below.'
              : checkpoint
                ? // A review gate has the Continue button right above this line, so
                  // "type your answer" contradicts the affordance — and telling the
                  // user to type is the exact thing FS item 3 removed.
                  'Use Continue to proceed, or tell me what to change below.'
                : 'Type your answer in the box below.'}
          </p>
        )}
        </div>
    </div>
  )
}

/**
 * Follow-ups under a finished result (Putra's chat design; the same pattern the
 * referenced beautiful-ui library calls follow-up chips).
 *
 * The suggestions are the workflow's OWN plan phases, not invented prompts: those are
 * the things this workflow demonstrably knows how to do, and picking one sends it as a
 * normal message. Inventing next questions would be a model's job, not the surface's.
 * Capped at three so the list stays scannable under the result.
 */
function FollowUpSuggestions({
  phases,
  disabled,
  onPick,
}: {
  phases: WorkflowPlanPhase[]
  disabled: boolean
  onPick: (text: string) => void
}) {
  const suggestions = phases.map((phase) => phase.title.trim()).filter(Boolean).slice(0, 3)
  if (suggestions.length === 0) return null
  return (
    <div className="mt-3">
      <p className="text-xs font-medium text-muted-foreground">Follow-ups</p>
      <div className="mt-1 flex flex-col">
        {suggestions.map((suggestion) => (
          <button
            key={suggestion}
            type="button"
            disabled={disabled}
            onClick={() => onPick(suggestion)}
            className="flex items-center gap-2 border-t border-border py-2 text-left text-sm text-muted-foreground hover:text-foreground disabled:opacity-50"
          >
            <CornerDownRight className="size-3.5 shrink-0" aria-hidden />
            <span className="min-w-0 truncate">{suggestion}</span>
          </button>
        ))}
      </div>
    </div>
  )
}

/**
 * The sources gate (FS item 3 + Putra's Human-In-The-Loop frames, Figma 12535-136526).
 *
 * The run's triage already resolves which drawings and tags it will reason over; until
 * now that work stayed internal and the only way to see it was to click into a step. On
 * the demo call the gate the user actually wanted was exactly this — he said pulling up
 * the P&ID and asking him to validate it is a human-in-the-loop moment worth his time,
 * whereas continue-continue-continue is slower than a plain chatbot.
 *
 * Rendered in Putra's shape: one bordered checkbox row per source, resolved ones checked.
 * Unmatched historian tags are listed as NOT resolved rather than hidden — a gate that
 * only shows what worked is not a check, and those are precisely what the user would
 * correct. Correction happens in the composer ("tell me what to change"), which is why
 * this card carries no edit control of its own.
 */
function ResolvedSourcesGate({ sources }: { sources: WorkflowResolvedSources }) {
  // The three arrays are REQUIRED by the type and always constructed by
  // `resolvedSourcesPayload`, but this value arrives as parsed JSON off an SSE frame
  // — an untrusted boundary. A missing key would throw inside render and take the
  // whole chat panel down with it, so read them defensively rather than trusting the
  // wire to match the declaration.
  const pnids = (sources.pnids ?? [])
    .map((pnid) => pnid?.documentName ?? pnid?.documentId ?? '')
    .filter(Boolean)
  const tags = (sources.historianTags ?? []).map((tag) => tag?.tagName).filter(Boolean)
  const unmatched = (sources.unmatchedHistorianTags ?? []).filter(Boolean)
  if (pnids.length === 0 && tags.length === 0 && unmatched.length === 0) return null

  const row = (label: string, resolved: boolean) => (
    <div
      key={`${resolved ? 'y' : 'n'}:${label}`}
      className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm"
    >
      <span
        aria-hidden
        className={cn(
          'grid size-4 shrink-0 place-items-center rounded border',
          resolved ? 'border-foreground bg-foreground text-background' : 'border-border'
        )}
      >
        {resolved && <Check className="size-3" />}
      </span>
      <span className={cn('min-w-0 truncate', resolved ? 'text-foreground' : 'text-muted-foreground')}>
        {label}
      </span>
      {/* Same amber pair the result panel's warnings use (WorkflowResultPanel.tsx:707):
          amber-600 alone is low-contrast on the dark canvas. */}
      {!resolved && (
        <span className="ml-auto shrink-0 text-[11px] text-amber-700 dark:text-amber-500">
          not found
        </span>
      )}
    </div>
  )

  return (
    <div className="mt-3 flex flex-col gap-1.5">
      <p className="text-xs font-medium text-muted-foreground">
        Using these sources — is this the right data?
      </p>
      {pnids.map((name) => row(name, true))}
      {tags.map((tag) => row(tag, true))}
      {unmatched.map((tag) => row(tag, false))}
    </div>
  )
}

/**
 * The intake's document option, spelled exactly as the backend offers it (`INTAKE_OPTIONS` in
 * `workflows-v2/elicit.ts`). Matched rather than inferred: this is the one clarification whose
 * options are ACTIONS, and treating every option list as actions would turn candidate answers on
 * every other gate into buttons.
 */
/**
 * "Interview in progress" (Figma 645:174716), and the "Processing" state that follows it
 * (645:175741). The run is parked on the call, so this is the only thing on screen telling the
 * user the workflow is still theirs — it must not disappear while the call runs.
 */
export function InterviewWaitCard({
  phase,
}: {
  phase: 'in_progress' | 'processing' | 'timed_out'
}) {
  if (phase === 'timed_out') {
    return (
      <div className="rounded-xl border border-border bg-background p-3.5 text-center dark:bg-dark-card">
        <p className="text-sm font-medium text-foreground">No transcript arrived</p>
        <p className="mt-0.5 text-xs text-muted-foreground">
          The call may still be running. When it ends, answer this question with the transcript
          using &quot;or use a past interview&quot;.
        </p>
      </div>
    )
  }
  return (
    <div className="flex flex-col items-center gap-2 rounded-xl border border-border bg-background p-6 dark:bg-dark-card">
      <span className="grid size-8 place-items-center rounded-lg bg-muted text-foreground dark:bg-dark-muted">
        {phase === 'processing' ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <Hourglass className="size-4" />
        )}
      </span>
      <p className="text-sm font-medium text-foreground">
        {phase === 'processing' ? 'Processing' : 'Interview in progress'}
      </p>
    </div>
  )
}

/** The supplied method, rendered as the artifact it is rather than as a line of chat text. */
export function SuppliedMethodCard({
  name,
  source,
  role,
}: {
  name: string
  source: 'document' | 'interview'
  /**
   * WHAT THE FILE WAS FOR. Absent ⇒ the method, which is what every upload was labelled before a
   * clarification card could take one.
   */
  role?: 'method' | 'data'
}) {
  const Icon = source === 'interview' ? Mic : FileText
  return (
    <div className="flex items-center gap-2.5 rounded-xl border border-border bg-background p-3 dark:bg-dark-card">
      <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-muted text-foreground dark:bg-dark-muted">
        <Icon className="size-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-foreground" title={name}>
          {name}
        </span>
        <span className="block text-xs text-muted-foreground">
          {source === 'interview' ? 'Interview transcript' : 'Document'} ·{' '}
          {role === 'data' ? 'supplied to this run' : 'supplied as the method'}
        </span>
      </span>
    </div>
  )
}

/**
 * "Share your Google Meet link" (Figma 645:173503). The link is typed into its own card rather
 * than the composer: at a park the composer answers the CLARIFICATION, so a URL typed there would
 * be submitted as the method text.
 */
export function MeetLinkCard({
  busy,
  onSubmit,
}: {
  busy: boolean
  /** Resolves false when the call could not be started, so the card can be used again. */
  onSubmit: (url: string) => Promise<boolean>
}) {
  const [url, setUrl] = useState('')
  const [sent, setSent] = useState(false)
  const submit = async () => {
    const trimmed = url.trim()
    if (!trimmed || busy || sent) return
    // Locked while in flight so one click cannot put two notetakers in the same call, then
    // RELEASED if the start failed — otherwise a bad link or a network blip left the input
    // disabled forever and the only way out was reloading the page (greptile P1 + Graphite
    // on #29843).
    setSent(true)
    const ok = await onSubmit(trimmed).catch(() => false)
    if (!ok) setSent(false)
  }
  return (
    <div className="rounded-xl border border-border bg-background p-3.5 dark:bg-dark-card">
      <p className="text-sm font-medium text-foreground">Share your Google Meet link</p>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <IntuiInput
          value={url}
          disabled={busy || sent}
          placeholder="Google meet link"
          aria-label="Google meet link"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => setUrl(event.target.value)}
          onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Enter') {
              event.preventDefault()
              void submit()
            }
          }}
          className="min-w-0 flex-1"
        />
        <IntuiButton size="sm" disabled={busy || sent || !url.trim()} onClick={() => void submit()}>
          Send
        </IntuiButton>
      </div>
    </div>
  )
}

const SEND_A_DOCUMENT = 'Send a document'
const GET_ON_A_CALL = 'Get on a call'
const ANSWER_THESE_QUESTIONS = 'Answer these questions'

/**
 * How each backend intake option is PRESENTED (Figma 645:163089). The keys are the backend's
 * strings — those are matched, never shown — and the label/hint are the design's copy. Keeping
 * them apart means renaming a button cannot break the recognition that makes these actions.
 */
const INTAKE_ROUTES: ReadonlyArray<{ option: string; label: string; hint: string }> = [
  {
    option: ANSWER_THESE_QUESTIONS,
    label: 'Answer clarification questions',
    hint: 'A few quick questions to fill the gap',
  },
  {
    option: SEND_A_DOCUMENT,
    label: 'Upload document',
    hint: 'Attach a file that describes the process',
  },
  {
    option: GET_ON_A_CALL,
    label: 'Request a call with our agent',
    hint: 'Talk it through with our AI agent',
  },
]

function WorkflowChatBlock({
  block,
  workflow,
  busy,
  onAnswer,
  onGenAnswer,
  onGenNavigate,
  activeGenBlockId,
  onEditSubmit,
  onSend,
  runInFlight = false,
  live = false,
  onFocusComposer,
  onViewReport,
  onSupplyDocument,
  onPickInterview,
  onRequestCall,
  onSubmitMeetLink,
}: {
  block: ChatBlock
  workflow: TroubleshootingWorkflowController
  busy: boolean
  /** Suppresses the ready-to-run affordance while a run is already going. */
  runInFlight?: boolean
  /**
   * This block belongs to the LIVE transcript, not the read-only scroll-back of an
   * earlier run. A persisted `workflow_ready` block replays in history, and without
   * this its Run button stayed actionable and would have started whatever workflow is
   * currently selected — a different one (greptile on #27043).
   */
  live?: boolean
  /** Puts the cursor in the composer for "add more information". */
  onFocusComposer?: () => void
  /** Opens the workflow's Report tab from a finished run's result card. */
  onViewReport?: (runId?: string) => void
  /** Answer the intake with a method document rather than typed prose. Live transcript only. */
  onSupplyDocument?: (file: File) => void | Promise<void>
  /**
   * Open the past-interview picker. Separate from `onSupplyDocument` because the source is a
   * catalog transcript, not a local file, and the run records it as an interview.
   */
  onPickInterview?: () => void
  /** Start the "get on a call" exchange (asks for the meeting link). */
  onRequestCall?: () => void
  /** Send the notetaker into the link the user pasted. */
  /** Resolves false when the call could not be started, so the card can unlock. */
  onSubmitMeetLink?: (url: string) => Promise<boolean>
  onAnswer: (text: string) => void | Promise<void>
  /**
   * Sends a brand-new message on the user's behalf, with the composer's routing.
   * Deliberately NOT `onAnswer`: that answers the ACTIVE clarification, so a
   * follow-up picked under a finished result would try to resume a closed run.
   */
  onSend: (text: string) => void | Promise<void>
  /** Answers the generation question the card is showing. */
  onGenAnswer: (text: string) => void | Promise<void>
  /** `< n of N >` navigation on the active question card. */
  onGenNavigate: (delta: number) => void
  /** The gen-questions block that is live (restored ones render inert). */
  activeGenBlockId: string | null
  /** Inline edit save: rewrite the message in place and regenerate. */
  onEditSubmit: (blockId: string, text: string) => void | Promise<void>
}) {
  if (block.kind === 'seed') {
    // Centered empty state (Figma "3. Create Workflow" — chat panel): shown
    // until the first message lands, then scrolls away like any other block.
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2 py-10 text-center">
        <span className="grid size-8 place-items-center rounded-lg bg-muted text-foreground dark:bg-dark-muted">
          <Workflow className="size-4" />
        </span>
        <p className="text-sm font-semibold text-foreground">Build your workflow.</p>
        <p className="text-sm text-muted-foreground">Use chat to describe your workflow</p>
      </div>
    )
  }

  if (block.kind === 'user') {
    return <UserMessageBlock block={block} busy={busy} onEditSubmit={onEditSubmit} />
  }

  if (block.kind === 'assistant') {
    // Figma: assistant speech is PLAIN text in the flow — no bubble, no
    // border, no avatar. Cards are reserved for questions and results.
    return <p className="text-sm leading-relaxed text-foreground">{block.text}</p>
  }

  if (block.kind === 'interview_wait') {
    return <InterviewWaitCard phase={block.phase} />
  }

  if (block.kind === 'attachment') {
    return <SuppliedMethodCard name={block.name} source={block.source} role={block.role} />
  }

  if (block.kind === 'meet_link') {
    return (
      <MeetLinkCard
        busy={busy}
        // No handler (the read-only history transcript) → the card cannot start a call, so it
        // reports failure rather than locking itself on a click that does nothing.
        onSubmit={(url) => onSubmitMeetLink?.(url) ?? Promise.resolve(false)}
      />
    )
  }

  if (block.kind === 'workflow_ready') {
    return (
      <PlanningDoneCard
        draft={workflow.draft}
        planPreview={workflow.planPreview}
        canRun={live && !runInFlight}
        // A re-run only once the CURRENT run is a different one than when this
        // card appeared — otherwise a regenerate mislabels a never-run plan.
        alreadyRan={Boolean(workflow.run) && workflow.run?.id !== block.runIdAtCreation}
        onRun={() => void workflow.startRun()}
        onAddInfo={() => onFocusComposer?.()}
      />
    )
  }

  if (block.kind === 'result') {
    // The result is the run's substantial artifact — render it full-width in the
    // flow (its own top border separates it), not constrained to a chat bubble.
    // Evidence→step linking works for the live run (current steps); historical
    // results render output-only since their step snapshots aren't retained.
    const isCurrent = workflow.run?.id === block.runId
    const run = {
      id: block.runId,
      status: block.status,
      finalOutput: block.finalOutput,
    } as unknown as WorkflowRunView
    return (
      <>
      <WorkflowResultPanel
        run={run}
        // The report is a TAB the user has to know exists; from the chat it is the
        // obvious next thing to look at once a run finishes (FS item 6). Bound to
        // THIS card's run, so a scroll-back result opens its own report.
        onViewReport={onViewReport && (() => onViewReport(block.runId))}
        steps={isCurrent ? workflow.steps : []}
        onSelectStep={
          isCurrent
            ? (step) =>
                workflow.selectGraphNode({
                  kind: 'step',
                  label: step.title,
                  title: step.title,
                  subtitle: workflowStatusLabel(step.status),
                  status: step.status,
                  isAgent: step.kind === 'agentic',
                  step,
                })
            : undefined
        }
      />
      {/* Live transcript only. Result blocks replay in cross-run scroll-back (the
          history filter drops only step/seed blocks), where `onSend` is a noop — so
          without this gate an earlier run's result showed enabled chips that did
          nothing. Same rule as the ready card's Run button. */}
      {live && (
        <FollowUpSuggestions
          phases={workflow.planPreview?.phases ?? []}
          disabled={busy}
          onPick={(text) => void onSend(text)}
        />
      )}
      </>
    )
  }

  if (block.kind === 'step') {
    // Legacy transcript block from before the design refresh — execution
    // detail now lives on the canvas nodes / node inspector, not in the chat.
    return null
  }

  if (block.kind === 'gen_questions') {
    return (
      <GenQuestionsCard
        block={block}
        busy={busy}
        active={block.id === activeGenBlockId}
        onAnswer={onGenAnswer}
        onNavigate={onGenNavigate}
      />
    )
  }

  if (block.kind === 'gen_question') {
    return (
      <GenQuestionCard
        block={block}
        busy={busy}
        onAnswer={onGenAnswer}
      />
    )
  }

  if (block.kind === 'clarification') {
    // Only the latest unanswered clarification is interactive: once the run has
    // moved past waiting_for_user (or a newer pause replaced it), freeze the card
    // so history isn't re-submittable.
    const isActive =
      workflow.run?.status === 'waiting_for_user' &&
      workflow.clarificationQuestion.trim() === block.question.trim()
    return (
      <ClarificationCard
        question={block.question}
        options={block.options ?? []}
        isActive={isActive}
        busy={busy}
        checkpoint={block.checkpoint === true}
        resolvedSources={isActive ? workflow.resolvedSources : null}
        onAnswer={onAnswer}
        onSupplyDocument={onSupplyDocument}
        onPickInterview={onPickInterview}
        onRequestCall={onRequestCall}
        askRole={workflow.askRole}
      />
    )
  }

  return null
}

function DetailsDialogShell({
  eyebrow,
  title,
  badge,
  closeLabel,
  onClose,
  children,
}: {
  eyebrow?: string
  title: string
  badge?: ReactNode
  closeLabel: string
  onClose: () => void
  children: ReactNode
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="flex max-h-[85vh] w-full max-w-3xl flex-col rounded-lg border border-border bg-background shadow-xl dark:bg-dark-card"
      >
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div>
            {eyebrow && (
              <div className="text-sm text-muted-foreground">{eyebrow}</div>
            )}
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-foreground">
                {title}
              </h2>
              {badge}
            </div>
          </div>
          <button
            type="button"
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            onClick={onClose}
            aria-label={closeLabel}
          >
            <X className="size-4" />
          </button>
        </div>
        <div className="min-h-0 overflow-auto p-5">{children}</div>
      </div>
    </div>
  )
}

export function ToolCallDetailsDialog({
  toolCall,
  onClose,
}: {
  toolCall: NormalizedToolCall
  onClose: () => void
}) {
  return (
    <DetailsDialogShell
      eyebrow={toolCall.step.title}
      title={toolCall.name}
      closeLabel="Close tool call details"
      onClose={onClose}
    >
      <DetailBlock title="Input" value={toolCall.input} />
      <DetailBlock title="Output" value={toolCall.output} />
      <DetailBlock title="Error" value={toolCall.error} />
      <DetailBlock title="Step Evidence" value={toolCall.step.evidence} />
      <DetailBlock title="Step Output" value={toolCall.step.output} />
      <DetailBlock title="Step Error" value={toolCall.step.error} />
      <DetailBlock title="Raw Tool Call" value={toolCall.raw} />
    </DetailsDialogShell>
  )
}

// Semantic badge color per status. completed_with_unknowns is `warning` (amber),
// NOT `success` — an honest "answered, but open questions" outcome should read as
// needs-attention, not done. Pure + exported so the mapping is unit-tested.
export function statusBadgeVariant(status: WorkflowRunStatus | string) {
  if (status === 'completed') return 'success'
  if (status === 'completed_with_unknowns' || status === 'waiting_for_user') return 'warning'
  if (status === 'failed' || status === 'blocked' || status === 'guardrail_stopped')
    return 'destructive'
  return 'outline'
}

export function StatusBadge({
  status,
}: {
  status: WorkflowRunStatus | string
}) {
  return <IntuiBadge variant={statusBadgeVariant(status)}>{workflowStatusLabel(status)}</IntuiBadge>
}

function WorkflowMessages({
  workflow,
}: {
  workflow: TroubleshootingWorkflowController
}) {
  if (!workflow.error && workflow.readinessWarnings.length === 0) return null

  return (
    <div className="space-y-3">
      {workflow.error && (
        <div className="flex gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-100">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <span>{workflow.error}</span>
        </div>
      )}
      {workflow.readinessWarnings.length > 0 && (
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-900 dark:border-yellow-900 dark:bg-yellow-950 dark:text-yellow-100">
          <div className="font-medium">KG scope warnings</div>
          <ul className="mt-1 list-disc space-y-1 pl-5">
            {workflow.readinessWarnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

/** Animated "Planning" row shown while the agent generates the workflow draft. */
/** The Intuigence logo dot used by the agent-status lines (Figma). */
function AgentDot() {
  return (
    <img src="/images/intuai-logo-icon.svg" alt="" aria-hidden className="size-4 shrink-0" />
  )
}

/**
 * "Planning ▾" (Figma Generating screens): the orange agent indicator with a
 * collapsible checklist of what the generation pipeline is doing. Expanded by
 * default, collapsible like any agentic chat trace.
 */
function PlanningIndicator({
  stages = [],
  paused = false,
}: {
  stages?: Array<{ label: string; done: boolean }>
  /** Parked on clarifying questions: keep the checklist, drop the live cues. */
  paused?: boolean
}) {
  const [open, setOpen] = useState(true)
  const Chevron = open ? ChevronDown : ChevronRight
  return (
    <div>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        disabled={stages.length === 0}
        className="flex items-center gap-2 text-sm font-medium text-orange-600 dark:text-orange-500"
      >
        <AgentDot />
        <span className="animate-pulse">Planning</span>
        {stages.length > 0 && <Chevron className="size-4 text-muted-foreground" />}
      </button>
      {open && stages.length > 0 && (
        <div className="mt-2 overflow-hidden rounded-lg border border-border">
          {stages.map((stage, index) => {
            // The first not-done stage is the one IN FLIGHT — show a spinner
            // there so the checklist reads as live progress, not a static
            // list. Paused (waiting on the user's answers) nothing is in
            // flight: every row renders its honest checkbox state.
            const activeIndex = paused ? -1 : stages.findIndex((candidate) => !candidate.done)
            const inFlight = index === activeIndex
            return (
              // A read-only status row, not a form control — <label> would
              // imply an activatable association with the decorative checkbox.
              <div
                key={stage.label}
                className="flex items-center gap-2.5 border-b border-border px-3 py-2.5 text-sm text-foreground last:border-b-0"
              >
                {inFlight ? (
                  <Loader2 className="size-4 shrink-0 animate-spin text-orange-600" aria-hidden />
                ) : (
                  <Checkbox checked={stage.done} disabled aria-hidden />
                )}
                <span>{stage.label}</span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

/**
 * Run-in-flight line (Figma "Answered" screen): the chat stays a simple
 * conversation while the run executes — per-step status streams onto the
 * CANVAS nodes, the chat just shows the agent is working.
 */
function ThinkingIndicator() {
  return (
    <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
      <AgentDot />
      <span className="animate-pulse">Thinking...</span>
    </div>
  )
}

/**
 * Plain-language summary of the generated plan (the design's "Done Creating
 * Workflow" copy), derived from the REAL plan preview: node count, span, and
 * which nodes pause for human review.
 */
function planningSummary(planPreview: WorkflowPlanPreview | null): string {
  const phases = planPreview?.phases ?? []
  if (phases.length === 0) {
    return 'The workflow draft is ready. The graph will populate on the canvas when the run emits workflow steps.'
  }
  const first = phases[0]?.title
  const last = phases[phases.length - 1]?.title
  const hitl = phases.filter((phase) => phase.mayAskClarification)
  const parts: string[] = []
  parts.push(
    `Your ${planPreview?.title ?? 'workflow'} is ready with ${phases.length} node${phases.length === 1 ? '' : 's'}` +
      (phases.length > 1 && first && last ? `, from ${first} through ${last}.` : '.')
  )
  if (hitl.length > 0) {
    if (isV2Plan(planPreview)) {
      // SAY WHAT IT DOES, not "pause and wait for your review". A v2 step does not stop for
      // sign-off: it asks a question when it cannot answer — nothing found, or nothing about the
      // equipment you named — and waits for that answer. Promising a review gate on every marked
      // node is the misinformation that kept this flag switched off for v2 in the first place.
      parts.push(
        `${hitl.length} node${hitl.length === 1 ? '' : 's'} can ask you for input: ` +
          `${hitl.map((phase) => phase.title).join(', ')}. ${hitl.length === 1 ? 'It pauses' : 'They pause'} ` +
          'only when the plant data cannot answer — otherwise the workflow runs straight through.'
      )
    } else {
      parts.push(
        `${hitl.length} node${hitl.length === 1 ? ' has' : 's have'} Human in the Loop enabled: ` +
          `${hitl.map((phase) => phase.title).join(', ')}. These are the points where the workflow ` +
          'will pause and wait for your review before continuing. Everything else runs autonomously.'
      )
    }
  } else {
    parts.push('Every node runs autonomously — no human review points are configured.')
  }
  parts.push('You can adjust any node by clicking into it, or hit Run workflow to start.')
  return parts.join(' ')
}

/**
 * "AI Plant Support Engineer: Done Creating Workflow" (Figma "8. Report" chat): a
 * collapsible summary of the generated plan + the Run button. The node detail
 * lives on the canvas, not here. (Renamed from "Planning Agent" — Putra
 * 2026-07-15: the product has agent avatars, no "planning agent" concept.)
 */
function PlanningDoneCard({
  draft,
  planPreview,
  canRun,
  alreadyRan,
  onRun,
  onAddInfo,
}: {
  draft: GenerateWorkflowDraftResponse | null
  planPreview: WorkflowPlanPreview | null
  /** False while a run is already in flight — the affordance would be a no-op. */
  canRun: boolean
  /** A run exists for this workflow, so the offer is a RE-run, not a first run. */
  alreadyRan: boolean
  onRun: () => void
  onAddInfo: () => void
}) {
  // Collapsed by default: once creation is done the canvas is the star — the
  // summary stays one click away for whoever wants the prose.
  const [open, setOpen] = useState(false)
  const Chevron = open ? ChevronDown : ChevronRight
  return (
    <div>
      {/* Figma: the DONE header is muted — orange is reserved for the
          in-progress Planning indicator. The RUN affordance lives here as well as
          on the canvas: the canvas pill sits inside the zoomable step chart and at
          real step counts is easy to miss entirely, so a first-time user finished
          generating and had no visible way forward (demo call, Tom Chunat 8:12 —
          FS item 4). Typing "run the workflow" already worked; nothing said so. */}
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 text-sm font-medium text-muted-foreground"
      >
        <AgentDot />
        {AGENT_ALIASES[AgentId.PLANT_SUPPORT]?.displayName ?? 'AI Plant Support Engineer'}: Done Creating Workflow
        <Chevron className="size-4" />
      </button>
      {open && (
        <div className="mt-2">
          <p className="text-sm leading-relaxed text-foreground">
            {planningSummary(planPreview)}
          </p>
          <DraftValidationSummary draft={draft} />
        </div>
      )}
      {canRun && (
        <div className="mt-2 rounded-lg border border-border bg-muted/30 p-3">
          {/* Once this workflow HAS run, "ready" is a false claim — the card sits
              above the finished result, so it must read as a re-run, not as a
              workflow that has yet to start. */}
          <p className="text-sm text-foreground">
            {alreadyRan
              ? 'Run this workflow again?'
              : 'Your workflow is ready. Do you want to run it?'}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <IntuiButton size="sm" leftIcon={<Play />} onClick={onRun}>
              {alreadyRan ? 'Run again' : 'Run workflow'}
            </IntuiButton>
            <IntuiButton size="sm" variant="outline" onClick={onAddInfo}>
              Add more information
            </IntuiButton>
          </div>
        </div>
      )}
    </div>
  )
}

function DraftValidationSummary({
  draft,
}: {
  draft: GenerateWorkflowDraftResponse | null
}) {
  // A valid draft needs no callout — the plan + Run button already imply it's
  // ready. Only surface a warning when validation actually fails, in plain
  // language (no internal step paths or template ids).
  // Only warn when there are concrete issues to list — a bare header with no
  // detail reads as confusing, and a failed validation always carries issues.
  // `validation?` — a draft is not required to carry one, and reading it unguarded took the
  // whole page down: the registry-backed canvas (#28932) returns definition + planPreview with
  // no validation, so expanding "Done Creating Workflow" threw
  // `Cannot read properties of undefined (reading 'ok')` and React rendered the 500 page. Same
  // shape as the `planPreview.phases` crash in #28780 — read the response defensively, because
  // a producer that omits a field is a deploy away, and a callout is not worth a dead page.
  const issues = draft?.validation?.ok === false ? (draft.validation.issues ?? []) : []
  if (issues.length === 0) return null
  return (
    <div className="mt-3 rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-xs text-yellow-900 dark:border-yellow-900 dark:bg-yellow-950 dark:text-yellow-100">
      <div className="font-medium">This draft has issues that may prevent it from running</div>
      <ul className="mt-1 list-disc space-y-1 pl-4">
        {issues.map((issue) => (
          <li key={`${issue.path}:${issue.message}`}>{issue.message}</li>
        ))}
      </ul>
    </div>
  )
}

function DetailBlock({ title, value }: { title: string; value: unknown }) {
  if (value === undefined || value === null || value === '') return null
  return (
    <section className="mb-4">
      <h3 className="mb-1.5 text-sm font-medium text-foreground">{title}</h3>
      <pre className="max-h-56 overflow-auto rounded-lg bg-muted p-3 text-xs text-foreground">
        {typeof value === 'string' ? value : JSON.stringify(value, null, 2)}
      </pre>
    </section>
  )
}
